// src/belediye/components/mesajlasma/ChatThread.js
// Kurum içi sohbet — mesaj alanı. Sistem temasıyla (WhatsApp değil).
//
// Özellikler: after_id delta poll (4sn; her 5. tur tam tazeleme) · TEK TİK
// (okununca altın vurgu) · animasyonlu "yazıyor…" · bağlam başlığı (grup adı +
// katılımcı avatarları / görev başlığı) · bağlama-göre hızlı-aksiyon çipleri
// (Görev Ver sihirbazı / Ne durumdayız? / İşe Başladım / Tamamlandım / Başvuru
// bağla) · resim & dosya ekleme · konum · referans
// kartları. variant: "dock" | "page" | "task".

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowUturnLeftIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  DocumentIcon,
  EllipsisVerticalIcon,
  LinkIcon,
  LockClosedIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PhotoIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  TrashIcon,
  UserGroupIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// FAZ-51: görev ikonu TEK sözlükten (kavram değişince her yer değişsin)
import { TaskIcon } from "../icons/appIcons";
import { toast } from "react-toastify";
// FAZ-60: yarım kalan mesaj (taslak) — okuma/yazma tek kaynaktan
import { taslakOku, taslakYaz } from "./taslak";
// FAZ-64: "mesaja atla" köprüsü — görev detayındaki medyadan ilgili mesaja.
import { MESAJLASMA_GOTO_EVENT } from "../../services/mesajlasmaBridge";
// FAZ-66: paylaşılan konumun baloncuktaki gösterimi — görev konumuyla ORTAK.
import KonumBlok from "./KonumBlok";
// FAZ-66: konum SEÇME ekranı — görev sihirbazındakiyle AYNI bileşen.
import KonumSecici from "./KonumSecici";
// FAZ-66: mobil/dikey tablet eşiği (otomatik GPS kararı) — tek kaynak.
import { tekPencereMi } from "./olcu";

import {
  addParticipant,
  addSahaPersonel,
  deleteMessage,
  fetchMessages,
  hideConversation,
  renameConversation,
  postTyping,
  removeMember,
  searchContacts,
  sendMessage,
  uploadChatFiles,
} from "../../services/mesajlasmaApi";
import {
  addComment,
  askStatus,
  fetchTaskDetail,
  taskAction,
  updateTask,
} from "../../services/internalTasksApi";
import {
  ASSIGNEE_PRESETS,
  PRESET_QUESTIONS,
  dueText,
  statusLabel,
  statusTone,
  stripClass,
} from "../tasks/GorevBits";
import usePermissions from "../../hooks/usePermissions";
import { playMsjReceived, playMsjSent, useMsjSoundMuted } from "./mesajlasmaSound";
import useMsjFeatures from "./useMsjFeatures";
import rehberBolumleri, { rehberSatiri } from "./rehberGruplari";
import Avatar, { AvatarGroup } from "./Avatar";
// FAZ-55/19: ek gösterimi ortak modülde — sohbet baloncuğu, teslim/düzeltme blok
// kartı ve GÖREV KARTI (RefMessageCard) aynı ızgarayı kullanır.
import { AttachmentGrid, Lightbox } from "./EkGrid";
import ConfirmDialog from "./ConfirmDialog";
import DuePicker from "./DuePicker";
import TaskComposer, { EkSecici } from "./TaskComposer";
import WizardQuestion from "./WizardQuestion";
import { TaskMessageCard, AppMessageCard } from "./RefMessageCard";

// FAZ-37 (12 MD §5 + FAZ-37 §3.4): backend locked_reason → kullanıcıya gösterilen
// TAM METİN (backend'in 403 detail metninden FARKLI — bu proaktif, salt-okunur
// bant; POST denemesi YOK). Aynen brief §3.4'teki metinler.
const LOCK_MESAJLARI = {
  ozellik_kapali: "Sohbet özelliği kapalı — geçmiş salt-okunur.",
  gorev_kapali: "Talep tamamlandı — sohbet salt-okunur.",
  basvuru_kapali: "Başvuru kapandı — sohbet salt-okunur.",
};

const trTime = (v) =>
  v ? new Date(v).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "";
const trDay = (v) =>
  v ? new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" }) : "";
const dayKey = (v) => (v ? String(v).slice(0, 10) : "");
// FAZ-55: görev şeridi altındaki KİŞİ çipine tıklayınca çıkan 3 hazır soru.
// Hepsi kişinin adıyla, doğrudan sohbete yazılır (ek onay yok — "Ne durumdayız?"
// çipinin eski davranışıyla aynı).
// FAZ-55/6: "Süre güncel mi?" KALDIRILDI — kullanıcı: "böyle bir soru yok,
// aslında bu saçma; 'süre güncellensin mi' diye bir soru yaz buraya." Doğrusu
// da bu: süreyi değiştirebilen taraf ATAYANDIR, dolayısıyla atayanın soracağı
// şey "süre doğru mu" değil, "sana süre açayım mı"dır.
const KISI_SORULARI = [
  { key: "durum", label: "Ne durumdayız?", metin: (ad) => `${ad} — bu talepte son durum nedir?` },
  { key: "sure", label: "Süre güncellensin mi?", metin: (ad) => `${ad} — süre güncellensin mi, ek süreye ihtiyacın var mı?` },
  { key: "foto", label: "Fotoğraf paylaşır mısın?", metin: (ad) => `${ad} — mevcut durumdan fotoğraf paylaşabilir misin?` },
];

// FAZ-55: görev şeridinin kısa tarihi (tek satıra sığsın → gün + kısa ay)
const trDate = (v) =>
  v ? new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "";

// TİK — İletildi (tek çentik, soluk) / Görüldü (çift çentik, altın).
// created_at ≤ read_until → okundu.
function Tick({ createdAt, receipts }) {
  const t = new Date(createdAt).getTime();
  const read = receipts?.read_until ? new Date(receipts.read_until).getTime() : 0;
  const okundu = read && t <= read;
  return (
    <span className={`msj-tick ${okundu ? "is-read" : ""}`} title={okundu ? "Görüldü" : "İletildi"}>
      {okundu ? (
        <svg viewBox="0 0 22 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M1 6.5 4.5 10 11 3" />
          <path d="M11 8.5 12 9.7 20 2" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 6.5 5.5 10 14 2" />
        </svg>
      )}
    </span>
  );
}

// FAZ-55/19: ChatImage / Lightbox / AttachmentGrid / FilePill buradan ÇIKTI —
// artık ./EkGrid modülünde. Sebebi: görev kartı (RefMessageCard) ayrı dosya
// olduğu için ek ızgarasını oraya da çizmek gerekiyordu ve kuralın iki kopyası
// olması, "üç resim yan yana" düzeninin bir gün yalnız bir yerde değişmesi
// demekti. Tek kaynak: EkGrid.

// FAZ-55/17 — SONUÇ MESAJLARININ SÖZLÜĞÜ. Görevin durumunu değiştiren her olay
// sohbette aynı BLOK KART biçiminde görünür; değişen yalnız başlık, "kim"
// etiketi ve renk. Kullanıcı: "olur da bu tür mesajların hepsi farklı şekilde
// blok olarak gitsinler — iptal, tamamlanma, onay vb."
const SONUC_TONLARI = {
  teslim: { durum: "Onay bekliyor", kim: "Onaya gönderen", notEtiket: "Açıklama" },
  onay: { durum: "Tamamlandı", kim: "Onaylayan", notEtiket: "Onay notu" },
  iptal: { durum: "İptal edildi", kim: "İptal eden", notEtiket: "Gerekçe" },
  duzeltme: { durum: "Düzeltme istendi", kim: "İsteyen", notEtiket: "Açıklama" },
  sure: { durum: "Süre güncellendi", kim: "Güncelleyen", notEtiket: "Yeni süre" },
  yeniden: { durum: "Yeniden açıldı", kim: "Açan", notEtiket: "Açıklama" },
};

// ── FAZ-81 — YOKLAMA, DEĞİŞİKLİK YOKSA HİÇBİR ŞEYİ YENİDEN ÇİZMEZ ────────
//
// Kullanıcı (2026-07-30): "hıçkırık gibi sürekli sayfa hareket ediyor; sistemin
// genelinde bir yenilenme yapısı var, kaldıralım."
//
// ÖLÇÜM (gerçek tarayıcı, görev detayı, 60 sn): `messages/` ucuna **15 istek**
// (4 sn'de bir). Sorun isteklerin kendisi değildi — `apply()` her tikte
// setMessages/setTyping/setReceipts/setParticipants/setTopicRef çağırıyor ve
// hepsine YENİ REFERANS veriyordu. Yeni mesaj olmasa bile React tüm sohbeti
// (şerit, menüler, baloncuklar) 4 saniyede bir yeniden çiziyordu: ekrandaki
// "hıçkırık" buydu. Sayfaya GÖMÜLÜ sohbette (görev detayı) etki daha görünür,
// çünkü orada sohbet bir sayfanın parçası.
//
// Çözüm: yoklama canlı KALIR (mesajın gecikmesi sohbeti bozar — FAZ-65'te
// kullanıcı bu istisnayı onaylamıştı), ama durum yalnız GERÇEKTEN değişince
// yazılır. Değişmediyse ÖNCEKİ REFERANS döner → React hiçbir şey çizmez.
const ayniMi = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return a == null && b == null;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false; // döngüsel/serileşmeyen → değişmiş say (güvenli taraf)
  }
};

export default function ChatThread({
  conversationId,
  initialTitle = "",
  variant = "dock",
  onNavigate,
  onBack,
  onHidden,
  // FAZ-38: biten görev kilit bandında yeni görev oluşunca YERİNDE GEÇİŞ —
  // aktif sohbeti yeni görevin sohbetine çevirir (dock/sayfa). Verilmezse
  // (GorevDetayPage, variant="task") yeni görevin DETAY sayfasına navigate edilir.
  onSwitchConversation,
  // Görev durumu SOHBETTEN değiştiğinde (başla/tamamla/süre) haber ver — görev
  // detay sayfası kendi redux kopyasını tazeler, yoksa sol sütun bayat kalır.
  onTaskChanged,
}) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const msjFeatures = useMsjFeatures();

  const viewerId = useMemo(() => {
    try {
      return Number(JSON.parse(localStorage.getItem("user") || "null")?.id) || 0;
    } catch {
      return 0;
    }
  }, []);

  const [title, setTitle] = useState(initialTitle);
  // FAZ-114: sohbete ELLE ad verildi mi? Talep sohbetinde başlık normalde
  // talepten türetilir; elle ad varsa o türetim devre dışı kalır.
  const [adElle, setAdElle] = useState(false);
  const [kind, setKind] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [topicRef, setTopicRef] = useState(null);
  const [messages, setMessages] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [typing, setTyping] = useState([]);

  // FAZ-60: açılışta yarım kalan metin geri gelir. ChatThread her sohbet için
  // `key={conversationId}` ile YENİDEN KURULDUĞU için başlangıç değeri doğru
  // sohbetin taslağıdır (bileşen sohbetler arasında taşınmaz).
  const [text, setText] = useState(() => taslakOku(conversationId));
  const [sending, setSending] = useState(false);
  const [staged, setStaged] = useState([]); // gönderim bekleyen ekler (önizleme)
  // FAZ-67 — TEK AÇIK POPUP. Kullanıcı (2026-07-29): "ataç ikonuna tıklayınca,
  // sonra diğer görevler menüsüne tıklayınca her ikisi de açık kalıyor."
  // Sebep: her menünün KENDİ boolean'ı vardı, kimse ötekini bilmiyordu. Çözüm
  // iki boolean'ı senkron tutmak değil, ONLARI TEK BİR DEĞERE indirmek: aynı
  // anda en fazla bir menü açık olabilir, çünkü değer tek.
  //   null | "ek" (ataç) | "aksiyon" (görev/başvuru)
  const [composerMenu, setComposerMenu] = useState(null);
  const [konumAcik, setKonumAcik] = useState(false);
  const [taskFlowOpen, setTaskFlowOpen] = useState(false); // C1: adım-adım görev composer'ı açık mı
  const [askOpen, setAskOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false); // G13: "Tamamla" sihirbazı açık mı
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true); // geçmişe sayfalanacak eski mesaj var mı
  const [taskDetail, setTaskDetail] = useState(null); // C3b: sohbetteki görevin bilgi kartı
  const [statusOpen, setStatusOpen] = useState(false); // C2: atanan "durum bildir" paneli
  // FAZ-55: atayanın onay akışı — null | "onay" | "duzeltme"
  // FAZ-55/15: "iptal" (görevi iptal et) ve "yeniden" (iptali geri al) eklendi;
  // dördü de AYNI kapıdan geçer → aynı anda yalnız biri açık olabilir ve alttaki
  // normal mesaj kutusu hepsinde gizlenir (iki mesaj kutusu hatasının kökü).
  const [onayMode, setOnayMode] = useState(null);
  // FAZ-55: "onaya gönder" son onay modalı — null | { note }
  const [teslimOnay, setTeslimOnay] = useState(null);
  // FAZ-55: üstteki görev şeridi açık mı (tıklayınca aşağı doğru genişler)
  const [seritAcik, setSeritAcik] = useState(false);
  // FAZ-55/6: şeritteki tek satırlık metin GERÇEKTEN kırpıldı mı? Kullanıcı:
  // "şerit hâlindeyken zaten açıklamanın tamamını görüyorsa akordiyonda bir
  // daha gösterme." Bunu tahmin etmek (karakter sayısı vb.) yanlış olur —
  // kırpma CSS'in işi, o yüzden DOM'dan ÖLÇÜLÜR.
  const [descKirpik, setDescKirpik] = useState(false);
  const seritDescRef = useRef(null);
  // FAZ-55/6: mesaj üstündeki üç-nokta menüsü hangi mesajda açık (id | null)
  const [msgMenuFor, setMsgMenuFor] = useState(null);
  // C2: süre güncelleme inline. FAZ-47: tarih ve saat AYRI tutulur
  // (null | {date: "YYYY-MM-DD", time: "HH:mm" | ""}). Eskiden tek bir
  // datetime-local'dı ve saatsiz görevde "00:00" ön-dolgusu kaydedilip
  // görev kartı gün boyu yanlış "Süre doldu" gösteriyordu.
  const [dueEdit, setDueEdit] = useState(null);
  const [lightbox, setLightbox] = useState(null); // D1: açık görsel ({ att, src }) | null
  const [membersOpen, setMembersOpen] = useState(false); // başlık 3-nokta → katılımcı + sil paneli
  const [confirmHide, setConfirmHide] = useState(false); // "Sohbeti sil" onayı
  // FAZ-52: 3 nokta panelindeki "Sohbet adı" alanı (tür ayrımı yok)
  const [adTaslak, setAdTaslak] = useState("");
  const [adKaydediliyor, setAdKaydediliyor] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // {id,name} — üye çıkarma onayı (kurucu)
  const [personMenuFor, setPersonMenuFor] = useState(null); // FAZ-38: görev kartı kişi chip mini-menüsü (id)
  const [sahaUnits, setSahaUnits] = useState(null); // FAZ-D: birim seçici (null=kapalı, []=yükleniyor)
  // FAZ-81: "Kişi ekle" seçicisi — null=kapalı, {loading|users|q} = açık.
  const [kisiEkle, setKisiEkle] = useState(null);
  const [kisiEkleniyor, setKisiEkleniyor] = useState(0); // eklenmekte olan user id
  const [soundMuted, setSoundMuted] = useMsjSoundMuted(); // GLOBAL ses anahtarı (tüm sohbetler)
  // FAZ-B rütbe bağlamı: can_assign_here (sohbette üst varsa atama UI gizli) +
  // viewer_is_creator (üye çıkarma yalnız kurucuda). Eski payload'da alanlar yok
  // → undefined = kısıt yokmuş gibi davran (geriye uyum).
  const [assignCtx, setAssignCtx] = useState({});
  // FAZ-37: writable/locked_reason payload'dan — composer yerine kilit bandı
  // çizmek için (varsayılan yazılabilir: eski payload'da alan yoksa kısıt yok).
  const [lockState, setLockState] = useState({ writable: true, lockedReason: "" });
  const [deleteFor, setDeleteFor] = useState(null); // FAZ-49: silinecek mesaj id'si

  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  // FAZ-74: `filesInputRef` KALDIRILDI. Ortak gizli bir <input type=file> tutup
  // onu JS ile tıklamanın kendisi kusurdu (bkz. EkSecici / attach menüsü
  // notları): her tüketici artık kendi <label>'ının içinde kendi girişini
  // taşıyor, dosya penceresini tarayıcı açıyor.
  const lastIdRef = useRef(0);
  const oldestIdRef = useRef(0); // yüklü en ESKİ mesaj (before_id ile geçmişe sayfalama)
  const loadedCountRef = useRef(0); // yüklü mesaj sayısı (tam tazelemede pencereyi korur)
  const seenMaxIdRef = useRef(0); // görülen en YÜKSEK mesaj id'si (geliş sesi için)
  const pollCountRef = useRef(0);
  const typingSentRef = useRef(0);
  const stickRef = useRef(true);

  const PAGE = 30; // A3: sayfa boyutu (kullanıcı reçetesi 30/50 — gecikme olmasın)

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // FAZ-64 — "MESAJA ATLA". Görev detayındaki bir görselin 💬 rozetine (ya da
  // "Sohbette onayla" yön levhasına) basılınca sohbet o mesaja kayar ve mesaj
  // kısa süre vurgulanır. Köprü olayla gelir; yüzeyler ChatThread'in içine
  // uzanmaz (mesajlasmaBridge deseni).
  //
  // Mesaj yüklü DEĞİLSE (geçmişe sayfalanmamışsa) sessizce hiçbir şey yapılmaz —
  // yanlış mesaja kaydırmaktansa yerinde kalmak daha iyidir.
  useEffect(() => {
    const onGoto = (e) => {
      const d = e?.detail || {};
      if (Number(d.conversationId) !== Number(conversationId)) return;
      const kok = scrollRef.current;
      if (!kok) return;
      const hedef = kok.querySelector(`[data-mid="${Number(d.messageId)}"]`);
      if (!hedef) return;
      hedef.scrollIntoView({ block: "center", behavior: "smooth" });
      hedef.classList.add("msj-row--vurgu");
      window.setTimeout(() => hedef.classList.remove("msj-row--vurgu"), 1600);
    };
    window.addEventListener(MESAJLASMA_GOTO_EVENT, onGoto);
    return () => window.removeEventListener(MESAJLASMA_GOTO_EVENT, onGoto);
  }, [conversationId]);

  const apply = useCallback((data, replace) => {
    const gelen = Array.isArray(data?.messages) ? data.messages : [];
    for (const m of gelen) if (m.id > lastIdRef.current) lastIdRef.current = m.id;
    // FAZ-F: id-bazlı dedup — pokeMessages + eşzamanlı poll aynı mesajı iki kez
    // eklemesin (mükerrer balon / React key uyarısı).
    setMessages((prev) => {
      if (replace || !prev) return gelen;
      if (gelen.length === 0) return prev; // FAZ-81: değişiklik yok → AYNI referans
      const have = new Set(prev.map((m) => m.id));
      const ekle = gelen.filter((m) => !have.has(m.id));
      return ekle.length ? [...prev, ...ekle] : prev;
    });
    if (data?.receipts) setReceipts((p) => (ayniMi(p, data.receipts) ? p : data.receipts));
    const yeniTyping = Array.isArray(data?.typing) ? data.typing : [];
    setTyping((p) => (ayniMi(p, yeniTyping) ? p : yeniTyping));
    if (data?.title) setTitle(data.title);
    if (data && "title_locked" in data) setAdElle(!!data.title_locked);
    if (data?.kind) setKind(data.kind);
    if (Array.isArray(data?.participants)) {
      setParticipants((p) => (ayniMi(p, data.participants) ? p : data.participants));
    }
    setTopicRef((p) => (ayniMi(p, data?.topic_ref || null) ? p : data?.topic_ref || null));
    if (data && "can_assign_here" in data) {
      setAssignCtx({
        canAssignHere: data.can_assign_here,
        isCreator: data.viewer_is_creator,
        // FAZ-114: ad değiştirme kapısı ARTIK backend'den TEK bayrak olarak
        // gelir (izin + kurucu birleşimi). Eski payload'da alan yoksa kurucuya
        // düşülür — geriye uyum; sunucu güncellenince gerçek kural devralır.
        canRename:
          "viewer_can_rename" in data
            ? !!data.viewer_can_rename
            : !!data.viewer_is_creator,
      });
    }
    // FAZ-37: eski payload'da alan yoksa yazılabilir say (geriye uyum).
    if (data && "writable" in data) {
      setLockState({ writable: data.writable !== false, lockedReason: data.locked_reason || "" });
    }
  }, []);

  // FAZ-F/R13: aksiyon (askStatus/addComment/taskAction) sonrası yeni mesajı ~4s
  // poll'ü beklemeden ANINDA getir — delta fetch + ekle (kendi mesajım; geliş sesi yok).
  const pokeMessages = useCallback(async () => {
    try {
      const data = await fetchMessages(conversationId, { afterId: lastIdRef.current });
      if ((data?.messages || []).length) {
        apply(data, false);
        stickRef.current = true;
        requestAnimationFrame(scrollToBottom);
      }
    } catch {
      /* sessiz */
    }
  }, [conversationId, apply, scrollToBottom]);

  useEffect(() => {
    let alive = true;
    lastIdRef.current = 0;
    oldestIdRef.current = 0;
    loadedCountRef.current = 0;
    seenMaxIdRef.current = 0;
    pollCountRef.current = 0;
    setMessages(null);
    setReceipts(null);
    setHasMore(true);

    const tick = async ({ first = false } = {}) => {
      try {
        pollCountRef.current += 1;
        const full = first || pollCountRef.current % 5 === 0 || !lastIdRef.current;
        // Tam tazeleme: yüklü pencereyi KORU (geçmişe sayfalanmış eski mesajlar
        // düşmesin) → en az PAGE, en çok yüklü kadar iste. Delta: yalnız yeniler.
        const opts = full ? { limit: Math.max(PAGE, loadedCountRef.current) } : { afterId: lastIdRef.current };
        const data = await fetchMessages(conversationId, opts);
        if (!alive) return;
        // GELİŞ sesi: başkasından GERÇEKTEN yeni mesaj (id > görülen en yüksek).
        // İlk yükte SESSİZ; hem delta hem tam-tazelemede doğru çalışır (tam
        // tazelemede eski mesajlar seenMax'in altında kalır → tetiklemez).
        const gelen = data?.messages || [];
        const maxIn = gelen.reduce((mx, m) => (m.id > mx ? m.id : mx), 0);
        if (first) {
          seenMaxIdRef.current = maxIn;
        } else {
          const yeniBaskasindan = gelen.some(
            (m) => m.id > seenMaxIdRef.current && Number(m.author) !== viewerId
          );
          if (maxIn > seenMaxIdRef.current) seenMaxIdRef.current = maxIn;
          if (yeniBaskasindan) playMsjReceived();
        }
        if (full) lastIdRef.current = 0;
        const yeni = gelen.length > 0;
        apply(data, full);
        if ((first || yeni) && stickRef.current) requestAnimationFrame(scrollToBottom);
      } catch {
        /* poll sessiz */
      }
    };

    tick({ first: true });
    const iv = setInterval(() => {
      if (!document.hidden) tick();
    }, 4000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [conversationId, apply, scrollToBottom, viewerId]);

  // Yüklü pencere sınırlarını izle (en eski id + sayı) — sayfalama refleri.
  useEffect(() => {
    const arr = messages || [];
    loadedCountRef.current = arr.length;
    oldestIdRef.current = arr.length ? arr[0].id : 0;
  }, [messages]);

  // C3b: görev sohbetiyse görev DETAYINI bir kez çek (kart + atayan/atanan
  // ayrımı). Durum değişince (start/complete → topic_ref.task_status) tazelenir.
  // NOT: sıcak poll payload'ı lean kalsın diye topic_ref'i şişirmiyoruz.
  const topicTaskId = topicRef?.task_id || null;
  const topicTaskStatus = topicRef?.task_status || null;
  const reloadTaskDetail = useCallback(() => {
    if (!topicTaskId) return;
    fetchTaskDetail(topicTaskId).then(setTaskDetail).catch(() => {});
  }, [topicTaskId]);
  useEffect(() => {
    if (!topicTaskId || !msjFeatures.gorev) {
      setTaskDetail(null);
      return;
    }
    let alive = true;
    fetchTaskDetail(topicTaskId)
      .then((d) => alive && setTaskDetail(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [topicTaskId, topicTaskStatus, msjFeatures.gorev]);

  // A3: geçmişe sayfalama — yukarı kaydırınca eski PAGE mesajı ÖNE ekle,
  // kaydırma konumunu koru (yeni yükseklik − eski yükseklik).
  const loadOlder = useCallback(async () => {
    if (loadingOlder || !oldestIdRef.current) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevH = el ? el.scrollHeight : 0;
    try {
      const data = await fetchMessages(conversationId, { beforeId: oldestIdRef.current, limit: PAGE });
      const older = data?.messages || [];
      if (older.length < PAGE) setHasMore(false);
      if (older.length > 0) {
        setMessages((prev) => [...older, ...(prev || [])]);
        requestAnimationFrame(() => {
          const e2 = scrollRef.current;
          if (e2) e2.scrollTop = e2.scrollHeight - prevH;
        });
      }
    } catch {
      /* sessiz */
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, loadingOlder]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 70;
    if (el.scrollTop < 48 && hasMore && !loadingOlder && (messages || []).length > 0) {
      loadOlder();
    }
  };

  const pushOwn = (msg) => {
    if (!msg) return;
    if (msg.id > lastIdRef.current) lastIdRef.current = msg.id;
    if (msg.id > seenMaxIdRef.current) seenMaxIdRef.current = msg.id; // kendi mesajım geliş sesi tetiklemesin
    setMessages((prev) => [...(prev || []), msg]);
    stickRef.current = true;
    requestAnimationFrame(scrollToBottom);
    playMsjSent(); // GİDİŞ sesi — tüm giden mesajlar buradan geçer (metin/dosya/kalıp)
  };

  const onTextChange = (e) => {
    setText(e.target.value);
    const now = Date.now();
    if (now - typingSentRef.current > 3000) {
      typingSentRef.current = now;
      postTyping(conversationId);
    }
  };

  // FAZ-60 — TASLAK YAZIMI. Metin durulunca (300ms) yazılır; her tuşta
  // localStorage'a gitmek gereksiz. Gönderim `setText("")` yaptığı için taslak
  // AYRICA temizlenmez — boş metin satırı zaten siler (tek çıkış yolu, "silmeyi
  // unuttuğumuz bir dal" kalmaz).
  const textRef = useRef(text);
  textRef.current = text;
  useEffect(() => {
    if (!conversationId) return undefined;
    const t = setTimeout(() => taslakYaz(conversationId, text), 300);
    return () => clearTimeout(t);
  }, [conversationId, text]);
  // Kapanış anı KRİTİK: pencere kapanınca/rota değişince bileşen sökülür ve
  // yukarıdaki gecikmeli yazım İPTAL olur — tam da korumak istediğimiz an.
  // Bu yüzden sökülürken son metin koşulsuz yazılır.
  useEffect(() => () => taslakYaz(conversationId, textRef.current), [conversationId]);

  // R15: WhatsApp-stili otomatik büyüyen yazı alanı — her karakterde yükseklik
  // içeriğe göre ayarlanır (max 108px = CSS max-height ile birebir). text
  // programatik temizlenince/değişince de doğru sıfırlanır.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 108)}px`;
  }, [text]);

  // Çoklu ek seçimi → önizleme kuyruğuna al (henüz göndermez). Resimlerde küçük
  // thumbnail için objectURL; resim adlarını GÖSTERMEYİZ (kullanıcı isteği).
  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setStaged((prev) => [
      ...prev,
      ...files.map((file, i) => {
        const isImage = (file.type || "").startsWith("image/");
        return {
          id: `${Date.now()}-${prev.length + i}-${file.name}`,
          file,
          isImage,
          url: isImage ? URL.createObjectURL(file) : null,
        };
      }),
    ]);
  };

  const removeStaged = (id) => {
    setStaged((prev) => {
      const it = prev.find((s) => s.id === id);
      if (it?.url) URL.revokeObjectURL(it.url);
      return prev.filter((s) => s.id !== id);
    });
  };

  // Gönder: bekleyen ekler TEK MESAJ olarak gider (yazı = o mesajın gövdesi);
  // yoksa düz metin mesajı. "Aç/Gönder" tek düğme — ekleri doğrudan yollar.
  //
  // FAZ-55/19 — NORMAL SOHBETTE DE TEK MESAJ. Kullanıcı: "tek seferde üç tane
  // fotoğraf gönderilirse ve altına yazı yazılırsa ya da birkaç belge aynı anda
  // gönderilirse aynı mesaj içinde gitsin, farklı mesajlar hâlinde gitmesin."
  // Eskiden burada dosya başına BİR istek atılıyordu (yazı yalnız ilkine caption
  // olarak takılıyordu) → üç resim üç ayrı baloncuk. Görev akışları (teslim /
  // düzeltme) çoktan `uploadChatFiles` ile tek mesaja geçmişti; normal sohbet
  // geride kalmıştı. Artık ikisi de AYNI ucu kullanır; ızgara + kaydırmalı modal
  // kendiliğinden gelir (bkz. EkGrid).
  // FAZ-66 — KONUM MESAJI GÖNDER.
  // Yazı kutusunda bekleyen metin varsa AYNI mesajla gider: "burada
  // buluşalım" + harita tek baloncuk olur (çoklu ek kuralıyla aynı disiplin —
  // bir eylem, bir mesaj). Metin yoksa backend gövdeyi "📍 Konum" yapar.
  const konumGonder = async ({ latitude, longitude }) => {
    if (sending) return;
    setSending(true);
    try {
      const msg = await sendMessage(conversationId, text.trim(), null, {
        lat: latitude,
        lng: longitude,
      });
      setText("");
      setKonumAcik(false);
      pushOwn(msg);
    } catch (e) {
      toast.error(
        e?.response?.data?.latitude || e?.response?.data?.detail || "Konum gönderilemedi."
      );
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    const body = text.trim();
    if (sending) return;
    if (staged.length > 0) {
      setSending(true);
      try {
        pushOwn(await uploadChatFiles(conversationId, staged.map((s) => s.file), body));
        staged.forEach((s) => s.url && URL.revokeObjectURL(s.url));
        setStaged([]);
        setText("");
      } catch (err) {
        toast.error(err?.response?.data?.file || err?.response?.data?.detail || "Ek gönderilemedi.");
      } finally {
        setSending(false);
      }
      return;
    }
    if (!body) return;
    setSending(true);
    try {
      const msg = await sendMessage(conversationId, body);
      setText("");
      pushOwn(msg);
    } catch (e) {
      toast.error(e?.response?.data?.body || e?.response?.data?.detail || "Mesaj gönderilemedi.");
    } finally {
      setSending(false);
    }
  };

  // FAZ-49: kendi mesajını sil. Satır listede KALIR ("Bu mesaj silindi"), o
  // yüzden listeden çıkarmıyoruz — yerinde is_deleted işaretliyoruz.
  const doDeleteMessage = async () => {
    const mid = deleteFor;
    setDeleteFor(null);
    if (!mid) return;
    try {
      await deleteMessage(mid);
      setMessages((prev) =>
        (prev || []).map((m) =>
          m.id === mid
            ? { ...m, is_deleted: true, body: "", attachments: [], reactions: {} }
            : m
        )
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Mesaj silinemedi."
      );
    }
  };

  // FAZ-55/14 — "mesajı kopyala". Gövde yoksa (yalnız ek olan mesaj) dosya
  // adlarını kopyalar; boş mesaj kopyalanmaz. navigator.clipboard yalnız güvenli
  // bağlamda (https/localhost) var → gizli textarea yedeği bırakıldı, aksi hâlde
  // http üzerinden test edilen ortamda düğme sessizce ölürdü.
  const copyMsg = async (m) => {
    setMsgMenuFor(null);
    const metin =
      (m.body || "").trim() ||
      (m.attachments || []).map((a) => a.filename).filter(Boolean).join(", ");
    if (!metin) {
      toast.info("Kopyalanacak metin yok.");
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard yok");
      await navigator.clipboard.writeText(metin);
      toast.success("Mesaj kopyalandı.");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = metin;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Mesaj kopyalandı.");
      } catch {
        toast.error("Mesaj kopyalanamadı.");
      }
    }
  };

  // FAZ-67 — MENÜ AÇ/KAPA. Aynı düğmeye basmak kapatır, BAŞKA bir menüye
  // basmak öncekini kapatıp yenisini açar. Mesajın üç-nokta menüsü de kapanır:
  // "aynı anda tek popup" kuralı composer'la sınırlı değil, sohbetin tamamında
  // geçerli — iki açık kutu ekranı ve tıklama hedefini kirletiyordu.
  const menuAcKapa = (hangi) => {
    setMsgMenuFor(null);
    setComposerMenu((cur) => (cur === hangi ? null : hangi));
  };
  const mesajMenusu = (id) => {
    setComposerMenu(null);
    setMsgMenuFor((cur) => (cur === id ? null : id));
  };

  // Mesaj araçları HER mesajda sabit duruyor (hover'la belirmiyor) → açık kalan
  // bir menü listeyi kirletir. Dışarı tıklama VE Escape hepsini kapatır.
  // FAZ-67: kapsam composer menülerini de içeriyor (eskiden yalnız mesaj
  // araçlarıydı; ataç/görev menüleri dışarı tıklayınca açık kalıyordu).
  useEffect(() => {
    if (msgMenuFor === null && composerMenu === null) return undefined;
    const kapat = (e) => {
      const t = e.target;
      if (t?.closest?.(".msj-msgtools") || t?.closest?.(".msj-attach-wrap")) return;
      setMsgMenuFor(null);
      setComposerMenu(null);
    };
    const esc = (e) => {
      if (e.key !== "Escape") return;
      setMsgMenuFor(null);
      setComposerMenu(null);
    };
    document.addEventListener("mousedown", kapat);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", kapat);
      document.removeEventListener("keydown", esc);
    };
  }, [msgMenuFor, composerMenu]);

  const go = (path) => {
    if (onNavigate) onNavigate();
    navigate(path);
  };

  // ── Bağlama-göre hızlı aksiyonlar ──────────────────────────────────
  const taskId = topicTaskId;
  const taskStatus = topicTaskStatus;
  const others = participants.filter((p) => !p.is_self);
  // C2: atayan (ne durumdayız? SORAN) ↔ atanan (durum BİLDİREN) ayrımı.
  // Atanan = görevin kullanıcı-atananları arasında viewer varsa.
  const isDoer = !!(
    viewerId &&
    (taskDetail?.assignees || []).some((a) => a.type === "user" && Number(a.id) === viewerId)
  );

  const sendPreset = async (q) => {
    setAskOpen(false);
    try {
      if (taskId) {
        await askStatus(taskId, q); // görev sohbetine ask-status mesajı düşer
        playMsjSent(); // G6: hazır mesaj da ses çıkarsın (pushOwn yok)
        pokeMessages(); // FAZ-F/R13: poll'ü bekleme, hemen getir
      } else {
        pushOwn(await sendMessage(conversationId, q));
      }
      // G6: başarılı toast YOK — mesaj zaten sohbete düşüyor (kullanıcı isteği)
    } catch {
      toast.error("İletilemedi.");
    }
  };

  // C2: ATANAN durum bildirir — göreve YORUM olarak düşer (soru değil).
  const sendAssigneePreset = async (text) => {
    setStatusOpen(false);
    try {
      await addComment(taskId, text);
      playMsjSent(); // G6: hazır mesaj da ses çıkarsın
      pokeMessages(); // FAZ-F/R13: poll'ü bekleme, hemen getir
      // G6: başarılı toast YOK
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İletilemedi.");
    }
  };

  // FAZ-F/R12: durum bildirirken foto/dosya eklendiyse — SOHBET MESAJI olarak
  // (metin = caption) gönderilir; anında görünür (pushOwn). Foto yoksa addComment yolu.
  const clearStaged = () => {
    staged.forEach((s) => s.url && URL.revokeObjectURL(s.url));
    setStaged([]);
  };
  const sendStatusWithMedia = async (caption) => {
    if (staged.length === 0) return;
    setSending(true);
    try {
      // FAZ-55/16: dosyalar TEK mesajda gider (eskiden her dosya ayrı bir
      // baloncuktu — beş fotoğraflı bildirim sohbeti beş satır uzatıyordu).
      pushOwn(await uploadChatFiles(conversationId, staged.map((s) => s.file), caption));
      clearStaged();
      // G6: başarılı toast YOK (pushOwn zaten ses çıkardı)
    } catch (err) {
      toast.error(err?.response?.data?.file || err?.response?.data?.detail || "Ek gönderilemedi.");
    } finally {
      setSending(false);
    }
  };

  // FAZ-F: staged ek önizlemesi — hem normal composer hem "Durum bildir" alanında.
  const stagedPreview = (
    <div className="msj-staged">
      {staged.map((s) => (
        <div key={s.id} className={`msj-staged-item ${s.isImage ? "is-img" : "is-file"}`}>
          {s.isImage ? (
            <img src={s.url} alt="" className="msj-staged-thumb" />
          ) : (
            <span className="msj-staged-file">
              <DocumentIcon />
              <span className="msj-staged-name">{s.file.name}</span>
            </span>
          )}
          <button type="button" className="msj-staged-x" onClick={() => removeStaged(s.id)} title="Kaldır" aria-label="Kaldır">
            <XMarkIcon />
          </button>
        </div>
      ))}
    </div>
  );

  // FAZ-55/15: BAŞARI/BAŞARISIZLIK döner — zincirli akışlarda (düzeltme iste →
  // süre güncelle → ek yükle) ilk adım patlarsa gerisi çalıştırılmamalı.
  // Dönen değer: başarıda YANIT GÖVDESİ (en kötü ihtimalle {}), hatada null.
  // FAZ-55/19: yanıt `result_message_id` taşıyor — kilitli sohbette ekler
  // işlemden sonra O KARTA iliştirilecek (bkz. akisTamamla).
  const doTaskAction = async (action, body) => {
    try {
      const data = await taskAction(taskId, action, body);
      playMsjSent(); // G6: hazır mesaj da ses çıkarsın
      reloadTaskDetail(); // kart durumunu hemen tazele
      onTaskChanged?.(); // detay sayfasının sol sütunu da tazelensin
      pokeMessages(); // FAZ-F/R13: durum-değişim mesajını hemen getir
      return data || {};
    } catch (e) {
      toast.error(e?.response?.data?.note || e?.response?.data?.detail || "İşlem yapılamadı.");
      return null;
    }
  };

  // FAZ-38: TaskComposer başarıyla görev oluşturunca — YERİNDE GEÇİŞ. Dock/sayfa
  // onSwitchConversation ile YENİ görevin sohbetine geçer ("sanki aynı kişiye yeni
  // görev vermişiz gibi"; eski yazışma ekrandan kaybolur, Görevler→Tamamlananlar'da
  // kalır). Görev-detay varyantında (prop YOK) yeni görevin DETAY sayfasına gider;
  // eski kilitli sohbete HİÇBİR ŞEY yazılmaz.
  const handleTaskComposerDone = (task) => {
    setTaskFlowOpen(false);
    const yeniConv = task?.conversation_id;
    if (onSwitchConversation && yeniConv) {
      onSwitchConversation(yeniConv);
    } else if (!onSwitchConversation && task?.id && variant === "task") {
      go(`/yonetim/gorevler/${task.id}`);
    }
  };

  // E1: ATANAN süreyi değiştiremez → ek süre TALEP eder (göreve yorum olarak düşer).
  const requestExtension = async () => {
    try {
      await addComment(taskId, "Ek süre talep ediyorum — uygun görürseniz süreyi güncelleyebilir misiniz?");
      toast.success("Ek süre talebiniz iletildi.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İletilemedi.");
    }
  };

  // C2: "Süre güncellensin mi? → Evet" karşılığı — yetkili süreyi günceller.
  // FAZ-43: due_date + due_time. FAZ-47: saat ayrı alan; boşsa NULL gider
  // (gün sonu terminine denk gelir), asla "00:00" yazılmaz.
  // FAZ-55: DuePicker'dan gelen (tarih, saat) ile çağrılır; boş tarih = süreyi kaldır.
  const submitDueEdit = async (date, time) => {
    try {
      const d = date || "";
      const t = time || "";
      const payload = { due_date: d || null, due_time: d && t ? t : null };
      await updateTask(taskId, payload);
      toast.success(d ? "Süre güncellendi." : "Süre kaldırıldı.");
      setDueEdit(null);
      reloadTaskDetail();
      onTaskChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.due_date || e?.response?.data?.detail || "Süre güncellenemedi.");
    }
  };

  // ── FAZ-55/15 — GÖREV SONUÇ AKIŞLARI ────────────────────────────────────
  // Onayla ve kapat / Düzeltme iste / Görevi iptal et / Yeniden aç: DÖRDÜ DE
  // aynı sihirbaz kapısından geçer ve alttaki normal mesaj kutusunu kapatır.
  // Kullanıcı: "onayla ve kapat tıklayınca iki tane mesaj kutusu açılıyor…
  // tek mesaj kutusundan ayarlayalım, aynı görev atarkenki yapıdaki gibi."
  //
  // Ek süre tabanı: MEVCUT termin ile bugünün BÜYÜĞÜ. Geçmişte kalmış bir
  // termine "1 gün ekle" demek yine geçmiş bir tarih üretirdi.
  const ekSureIso = (gun) => {
    const bugun = new Date();
    bugun.setHours(12, 0, 0, 0);
    const mevcut = taskDetail?.due_date ? new Date(`${taskDetail.due_date}T12:00:00`) : null;
    const taban = mevcut && mevcut > bugun ? mevcut : bugun;
    const d = new Date(taban.getTime());
    d.setDate(d.getDate() + gun);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };

  // "Düzeltme iste" akışı üç adımlıdır (kullanıcı isteği): not → EK SÜRE →
  // fotoğraf. Gerekçesi kullanıcının kendi cümlesi: "düzeltme istenince ek süre
  // de eklenmek istenebilir." Görev açma sihirbazıyla AYNI bileşenler kullanılır
  // (DuePicker + EkSecici) — iki ayrı süre/ek deneyimi olmasın.
  const akisUcAdim = onayMode === "duzeltme" || onayMode === "yeniden_duzeltme";
  const akisNotZorunlu = akisUcAdim;
  const AKIS_BASLIK = {
    onay: "Onayla ve kapat",
    duzeltme: "Düzeltme iste",
    iptal: "Talebi iptal et",
    yeniden: "Talebi yeniden aç",
    yeniden_duzeltme: "Düzeltme iste",
  };
  const AKIS_PLACEHOLDER = {
    onay: "Onay notu (isteğe bağlı)…",
    duzeltme: "Neyin düzeltilmesi gerekiyor? (zorunlu)",
    iptal: "İptal gerekçesi (isteğe bağlı)…",
    yeniden: "Yeniden açma notu (isteğe bağlı)…",
    yeniden_duzeltme: "Neyin düzeltilmesi gerekiyor? (zorunlu)",
  };
  const akisAdimlari = () => {
    if (onayMode === null) return [];
    const notAdim = {
      key: "note",
      kind: "text",
      multiline: true,
      title: AKIS_BASLIK[onayMode],
      placeholder: AKIS_PLACEHOLDER[onayMode],
      required: akisNotZorunlu,
      optional: !akisNotZorunlu,
      maxLength: 2000,
    };
    if (!akisUcAdim) return [notAdim];
    return [
      notAdim,
      {
        key: "due",
        kind: "options",
        title: "Ek süre",
        optional: true,
        options: [
          { label: "1 gün ekle", patch: { due_date: ekSureIso(1) } },
          { label: "2 gün ekle", patch: { due_date: ekSureIso(2) } },
          { label: "3 gün ekle", patch: { due_date: ekSureIso(3) } },
          { label: "1 hafta ekle", patch: { due_date: ekSureIso(7) } },
        ],
        customPicker: {
          label: "Kendin süre belirle…",
          render: ({ onDone, onSkip }) => (
            <DuePicker
              initialDate={taskDetail?.due_date || ""}
              initialTime={(taskDetail?.due_time || "").slice(0, 5)}
              onDone={onDone}
              onSkip={onSkip}
            />
          ),
        },
      },
      {
        key: "ek",
        kind: "picker",
        title: "Fotoğraf veya belge",
        render: ({ onDone, onSkip }) => (
          <EkSecici
            adet={staged.length}
            onDosya={onPickFiles}
            onDone={() => onDone({})}
            onSkip={onSkip}
          />
        ),
      },
    ];
  };
  // Sıra ÖNEMLİ: önce durum değişir (iptal edilmiş görevin sohbeti kilitlidir →
  // yeniden açılmadan ne süre güncellenebilir ne ek yüklenebilir), sonra süre,
  // en sonda ekler. İlk adım başarısızsa gerisi hiç denenmez.
  const akisTamamla = async (a) => {
    const mod = onayMode;
    const note = (a.note || "").trim();
    setOnayMode(null);
    if (mod === null) return;
    if (akisNotZorunlu && !note) return;
    // FAZ-55/18 — SÜRE AYRI İSTEK DEĞİL. Kullanıcı: "tek seferde mesaj
    // gönderdiğime rağmen düzeltme + süre + tekrar düzeltme diye üç mesaj
    // gidiyor." Sebebi üç ayrı çağrıydı (request_changes → PATCH due →
    // ekleri yükle). Artık süre, düzeltme isteğinin GÖVDESİNDE gidiyor ve
    // aynı kartın "Süre" satırına yazılıyor.
    const sureGovde =
      a.due_date !== undefined ? { due_date: a.due_date, due_time: a.due_time || null } : {};
    const EYLEM = {
      onay: ["close_confirm", { note }],
      duzeltme: ["request_changes", { note, ...sureGovde }],
      iptal: ["cancel", { reason: note }],
      yeniden: ["reopen", { note }],
      // FAZ-55/19: KAPANMIŞ görevde "Düzeltme iste" de `reopen`'dır ve o da
      // süreyi AYNI istekte taşır. Kullanıcı: "düzenleme yapılınca sana dedim ki
      // düzeltme kartının içinde olsun — hâlâ farklı bir mesaj olarak gidiyor ya
      // da hiç gitmiyor; sürenin artırılıp artırılmadığını göremiyorum."
      // Sebebi buydu: `request_changes` FAZ-55/18'de tek karta indirilmişti ama
      // onaydan SONRAKİ düzeltme bu yoldan geçmiyor, ayrı bir PATCH atıyordu →
      // ikinci bir "Süre güncellendi" kartı (ya da PATCH 403 alırsa hiçbir şey).
      yeniden_duzeltme: ["reopen", { note, ...sureGovde }],
    };
    const [eylem, govde] = EYLEM[mod];

    // FAZ-55/17 — DÜZELTME İSTEĞİ DE TEK BLOK. Kullanıcı: "düzeltme yapıldıysa
    // burada da aynı mantıkta mesaj blok halinde gitsin, ayrı ayrı değil."
    // Sohbet YAZILABİLİRSE ekleri notla birlikte tek mesaj olarak yükleyip
    // id'yi aksiyona veriyoruz → backend ikinci mesaj yazmıyor, o mesajı
    // "duzeltme" tonuna boyuyor. İptal edilmiş görevde (yeniden_duzeltme)
    // sohbet KİLİTLİ olduğu için yükleme reopen'dan SONRAYA kalır.
    // Ekler: sohbet YAZILABİLİRSE aksiyondan ÖNCE (id aksiyona verilir → tek
    // kart). İptal edilmiş görevde sohbet kilitli olduğundan reopen'dan sonra.
    // NOT: `clearStaged()` bir React state güncellemesidir; bu fonksiyonun
    // içindeki `staged` değişkeni DEĞİŞMEZ. Yerel bayrak olmadan ikinci blok
    // aynı dosyaları BİR KEZ DAHA yüklüyordu — kullanıcının gördüğü üçüncü
    // mesaj buydu.
    let eklerGonderildi = staged.length === 0;
    // `hedefMesajId` verilirse ekler YENİ mesaj açmaz, o karta iliştirilir
    // (FAZ-55/19; kilitli sohbet dalı). Boşken ekler kendi kartını yazar ve
    // id'si aksiyona verilir.
    const ekleriYukle = async (hedefMesajId = null) => {
      setSending(true);
      try {
        const msg = await uploadChatFiles(
          conversationId,
          staged.map((st) => st.file),
          note,
          mod === "yeniden" || mod === "yeniden_duzeltme" ? "yeniden" : "duzeltme",
          hedefMesajId
        );
        eklerGonderildi = true;
        clearStaged();
        return msg;
      } catch (err) {
        toast.error(err?.response?.data?.file || err?.response?.data?.detail || "Ekler gönderilemedi.");
        return null;
      } finally {
        setSending(false);
      }
    };

    if (mod === "duzeltme" && !eklerGonderildi) {
      const msg = await ekleriYukle();
      if (msg?.id) {
        govde.message_id = msg.id;
        pushOwn(msg);
      }
    }

    const sonuc = await doTaskAction(eylem, govde);
    if (!sonuc) {
      clearStaged();
      return;
    }
    // Kilitli sohbetten geçen akışlarda (kapanmış/iptal görev) ekleri ancak
    // ŞİMDİ yükleyebiliriz — ama artık kendi baloncuğunu açmadan, işlemin
    // yazdığı sonuç kartına iliştirerek. Böylece "tek eylem = tek mesaj" kuralı
    // burada da bozulmuyor. (Eskiden burada ayrıca bir PATCH ile süre
    // güncelleniyordu; o da ikinci bir kart demekti — süre artık aksiyonun
    // gövdesinde gidiyor.)
    if (!eklerGonderildi) {
      const msg = await ekleriYukle(sonuc.result_message_id || null);
      if (msg) pushOwn(msg);
    }
    pokeMessages();
  };

  // FAZ-49: silme düğmesi izin + kilit türevli (sahiplik satır bazında `mine`).
  const canDeleteMsg = can("mesajlasma.message.delete") && lockState.writable;
  const gorevVerAcik = taskFlowOpen;
  // FAZ-B RÜTBE: hedefler yalnız atanabilir (alt rütbeli) katılımcılar; sohbette
  // benden üst biri varsa (can_assign_here=false) atama UI TAMAMEN gizli
  // (kullanıcı kararı). Eski payload'da alanlar yoksa kısıtsız davran.
  const assignableOthers = others.filter((p) => p.can_assign !== false);
  // B3: "Görev Ver" ATAMA gerektirir — yalnız tasks.create YETMEZ; atama izni
  // (tasks.assign_to_user) yoksa buton GÖRÜNMESİN (çalışmayan buton gösterme).
  // FAZ-37: gorevModulu yerine msjFeatures.gorev (flag + modül çifte kapısı).
  // FAZ-45: görev başlatma/spin-off — görev sohbetinde de (taskId dolu) çalışır.
  // handleTaskComposerDone yeni görevin KENDİ conversation'ına geçer (ayrı sohbet);
  // eskiden burada `!taskId` vardı (canGorevVer) — aktif görev sohbetinde
  // Hızlı Görev/Görev Ata menüsünü gizliyordu. Artık gizlemiyor.
  const canGorevBaslat =
    msjFeatures.gorev &&
    can("tasks.create") &&
    can("tasks.assign_to_user") &&
    assignableOthers.length > 0 &&
    assignCtx.canAssignHere !== false;
  // FAZ-47: görev aksiyon çiplerinin görünürlüğü backend bayraklarından okunur
  // (task detay serializer'ının `permissions` bloğu). Nesne-düzeyi kuralı burada
  // yeniden türetmek, dock varyantında ilgisiz üçüncü kişiye de çip gösterip
  // her tıklamada 403 döndürüyordu. Bayrak henüz gelmediyse (eski payload /
  // yükleme anı) çip çizilmez — çalışmayan buton göstermektense göstermemek.
  const tperms = taskDetail?.permissions || null;
  // FAZ-55 — ÇİPLER GÖREV ÖZELİNDE ROL'e göre ayrılır, izin koduna göre DEĞİL.
  // Kullanıcı kuralı: "aynı kişi bir sohbette atayan, başka sohbette atanan
  // olabilir; atayan 'ne durumdayız'/'süre güncelle' görür, atanan 'durum
  // bildir'/'süre iste'/'onaya gönder' görür — biri diğerininkini GÖRMEZ."
  // Bu yüzden atayan setine `!isDoer`, atanan setine `isDoer` kapısı konur.
  //
  // İkinci kapı: ATANAN, İŞE BAŞLAMADAN durum/süre çiplerini görmez — önce
  // "İşe Başladım". (Başlamadan durum bildirmek anlamsız; teşvik bandı da bunu
  // söylüyordu ama çipler yine de duruyordu.)
  const doerHazir = isDoer && taskStatus !== "yeni"; // işe başlamış atanan
  // C2: ATAYAN "Ne durumdayız?" SORAR — atanan sormaz.
  const canAsk = !!(taskId && !isDoer && tperms?.can_ask_status);
  // C2: ATANAN durum BİLDİRİR — ayrı kalıp seti (soru değil, yorum).
  const canReport = !!(taskId && doerHazir && tperms?.can_comment);
  // E1: SÜRE'yi yalnız ATAYAN düzenler; ATANAN değiştiremez → "Ek süre iste" ile TALEP eder.
  const canEditDue = !!(taskId && !isDoer && tperms?.can_update);
  const canRequestDue = !!(taskId && doerHazir && tperms?.can_comment);
  // FAZ-55: atanan işi onaya gönderince ATAYAN sohbette onay/düzeltme çiplerini
  // görür — onaylamak için görev detayına gitmek zorunda kalmasın.
  const canApprove = !!(taskId && !isDoer && tperms?.can_close_confirm);
  const canRequestChanges = !!(taskId && !isDoer && tperms?.can_request_changes);
  // FAZ-55/15 — İPTAL / YENİDEN AÇMA yalnız görevi VEREN tarafta.
  // Kullanıcı: "kendi kendisine görev atanan kişi onayla ve kapat ya da
  // düzeltme iste butonlarını görmesin." Kapı ikili: backend nesne-bayrağı
  // (`can_cancel`/`can_reopen` → tasks.cancel/tasks.reopen + makam) ve burada
  // `!isDoer` (aynı kişi hem atayan hem atanan olabilen görevlerde rol ayrımı).
  const canCancel = !!(taskId && !isDoer && tperms?.can_cancel);
  const canReopen = !!(taskId && !isDoer && tperms?.can_reopen);
  // İşe Başladım / Onaya Gönder: yalnız İŞİ YAPANA — hem dock'ta hem detayda.
  // (Atayanın onay/kapatma aksiyonları GorevDetayPage'in sol panelinde durur;
  // böylece çift kontrol olmaz — kullanıcı kararı 2026-07-19 + FAZ-47.)
  const startable = !!(taskId && tperms?.can_start);
  // FAZ-55/16 — BAŞLAMADAN HİÇBİR ÇİP YOK. Kullanıcı: "işe başladım butonuna
  // tıkladıktan sonra onaya gönder çipi açılsın; orada başka herhangi bir çip
  // olmasın." Görev "yeni" iken tek eylem işe başlamaktır; "Onaya Gönder"i
  // başlamadan göstermek, yapılmamış işi teslim etmeye davet ediyordu.
  const completable = !!(taskId && tperms?.can_complete && (!isDoer || doerHazir));
  // FAZ-37: "Başvuru Oluştur" menü maddesi başvuru özelliğine bağlanır (sıfır-iz).
  const canRefApp = msjFeatures.basvuru && can("mesajlasma.reference.application");
  // FAZ-38: biten görev bandındaki yeni-görev chip'leri — atama yetkilisi + alt
  // rütbeli hedef varsa (rütbe kuralı assignableOthers'ta uygulanır). Bu da
  // (canGorevBaslat gibi) görev SOHBETİNDE (taskId dolu) çalışır; FAZ-45'ten
  // önce yalnız bu (canYenidenGorev) çalışıyordu, canGorevVer !taskId ile kapalıydı.
  const canYenidenGorev =
    msjFeatures.gorev &&
    can("tasks.create") &&
    can("tasks.assign_to_user") &&
    assignableOthers.length > 0;
  // G5: görev kartı kişi chip'leri — AKTİF atananlar; viewer (self) + atayan
  // (created_by) HARİÇ; tek atanan varsa (2 kişilik görev) çip GÖSTERME (gereksiz).
  const aktifAtananlar = (taskDetail?.assignments || []).filter(
    (a) => a.is_active !== false && a.assignee_user
  );
  const gorevKisileri =
    taskId && aktifAtananlar.length >= 2
      ? aktifAtananlar
          .filter(
            (a) =>
              Number(a.assignee_user) !== viewerId &&
              Number(a.assignee_user) !== Number(taskDetail?.created_by)
          )
          .map((a) => ({
            id: a.assignee_user,
            name: a.assignee_user_display || "—",
            avatar: a.assignee_user_avatar || null,
          }))
      : [];

  // G4: görev sohbet başlığı — HIZLI görevde (title == açıklamanın ilk satırı)
  // açıklamayı, DETAYLI görevde başlığı göster. taskDetail yoksa topicRef'e düş.
  const gorevSohbetBaslik = () => {
    if (!taskId) return title || "Sohbet";
    // FAZ-114: sohbete ELLE ad verildiyse o ad kazanır — aşağıdaki türetim
    // (hızlı talepte açıklamanın ilk satırını göster) yalnız ad verilmemiş
    // sohbetler için vardır. Bu kapı olmadan, kişi sohbeti adlandırır ama
    // sohbetin kendi başlığında hiçbir değişiklik görmezdi.
    if (adElle && title) return title;
    const d = taskDetail;
    if (!d) return topicRef?.task_title || title || "Talep";
    const turetilmis = (d.description || "").trim().split("\n")[0];
    const detayli = d.title && d.title !== turetilmis;
    return (detayli ? d.title : d.description || d.title) || "Talep";
  };

  // NOT (FAZ-55/4): "kişinin kendi durumu" (Başladı / Onaya gönderdi / Bekliyor)
  // türetimi KALDIRILDI — kullanıcı kararı: "sorumlu, kapat bekliyor gibi
  // şeyleri açılınca gösterme, bunlara gerek yok, zaten detay sayfasında
  // görüyoruz." Akordiyonda kişiler yalnız ADIYLA çip olarak durur.

  // FAZ-55/4 — ŞERİDİN TEK SATIRLIK METNİ, iki parça hâlinde.
  // Kullanıcı kuralı: DETAYLI görevde "altına tek satır halinde başlığı veriyoruz,
  // başlığın yanına eğer yer varsa açıklamayı veriyoruz; açıklamanın tamamı
  // sığmazsa şerit orada biter." HIZLI görevde başlık zaten açıklamadan türetilir
  // → yalnız açıklama yazılır (aynı metni iki kez göstermek nizamı bozar).
  // Kırpma CSS'in işi (tek satır, taşan "…") — metni burada KESMİYORUZ ki
  // akordiyon açılınca tamamı görünebilsin.
  const seritMetin = (() => {
    const d = taskDetail;
    if (!d) return { baslik: "", aciklama: "" };
    const aciklama = (d.description || "").trim().replace(/\s+/g, " ");
    const turetilmis = (d.description || "").trim().split("\n")[0].slice(0, 80);
    const detayli = d.title && d.title !== turetilmis;
    if (!detayli) return { baslik: "", aciklama: aciklama || d.title || "" };
    return { baslik: d.title, aciklama };
  })();

  // FAZ-55/4 — AKORDİYONDAKİ BİLGİ ÇİPLERİ. Kullanıcı: "altında diğer bilgileri
  // görüyoruz — varsa haritanın konumu, varsa mahalle bilgisi, açık adres…
  // hepsi çiplerin içinde görünecek biçimde." Haritanın KENDİSİ burada YOK
  // (kullanıcı: "haritayı göstermene gerek yok, detay sayfasında görünebilir");
  // konum işaretlenmişse yalnız bir çip belirtir.
  const seritBilgiCipleri = (() => {
    const d = taskDetail;
    if (!d) return [];
    const out = [];
    if (d.unit_name) out.push({ k: "birim", metin: d.unit_name });
    // FAZ-55/8: çoklu mahalle — HER mahalle kendi çipi olur (tek çipte
    // virgülle dizmek uzun listede okunmaz hâle gelirdi). Liste boşsa eski
    // tekil alana düşülür (FAZ-55/8 öncesi görevler).
    const mahalleler =
      d.neighborhood_names?.length > 0
        ? d.neighborhood_names
        : d.neighborhood_name
        ? [d.neighborhood_name]
        : [];
    mahalleler.forEach((ad, i) => out.push({ k: `mahalle-${i}`, metin: ad }));
    if (d.address) out.push({ k: "adres", metin: d.address });
    if (d.latitude != null && d.longitude != null) {
      out.push({ k: "konum", metin: "Konum işaretli" });
    }
    return out;
  })();

  // FAZ-55/6 — ŞERİTTEKİ METİN KIRPILDI MI? scrollWidth > clientWidth ise tek
  // satıra sığmamış demektir; yalnız O ZAMAN akordiyonda tam metni gösteririz.
  // (jsdom'da iki ölçü de 0 → false; testler akordiyonu boş sanmaz çünkü
  // testler açıklamayı kırpılmış senaryoyla kurgulamaz.)
  useEffect(() => {
    const el = seritDescRef.current;
    if (!el) {
      setDescKirpik(false);
      return undefined;
    }
    const olc = () => setDescKirpik(el.scrollWidth > el.clientWidth + 1);
    olc();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(olc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [seritMetin.baslik, seritMetin.aciklama]);

  // FAZ-55/6 — KİŞİ ÇİPLERİ ARTIK AKORDİYONUN İÇİNDE. Eskiden şeridin altında
  // AYRI bir şerit daha vardı; kullanıcı: "alt tarafı ayrı bir alan gibi
  // tasarlıyorsun, böyle yapma." Çipler şeridin açılan hâline taşındı; kural
  // aynı kaldı: yalnız ATAYAN görür (canAsk), kendisi ve atayan listede yok,
  // tek atananlı (2 kişilik) görevde hiç çip çıkmaz.
  // FAZ-55/16 — İKİ AYRI LİSTE:
  //  • akordiyonKisileri: BİLGİ. Akordiyon açılınca görünen, tıklanamayan
  //    isimler (kullanıcı: "tüm katılımcıları görelim ama tıklanabilir değil").
  //  • asiliKisiler: EYLEM. Şeridin altında ASILI duran, tıklanınca kişiye
  //    özel soru gönderen çipler — yalnız ATAYAN'da ve yalnız EN AZ İKİ atanan
  //    varken (tek atananda muhatap zaten mesaj kutusunun karşısındaki kişi).
  const asiliKisiler = canAsk && gorevKisileri.length >= 2 ? gorevKisileri : [];
  // FAZ-55/19 — AYNI İSİM İKİ KEZ YAZILMAZ. Kullanıcı: "şeridin altında
  // görünüyor, ama akordiyon ağacında bir daha isimler görünüyor; burada
  // görünecekse akordiyonu açtığımızda görünmesin — iki farklı isim silsilesi
  // olmuş oluyor." Kural: asılı çipler VARSA akordiyon katılımcı YAZMAZ; yoksa
  // (tek atanan ya da atanan tarafın gözü) isimler yalnız akordiyonda durur.
  const akordiyonKisileri = asiliKisiler.length > 0 ? [] : gorevKisileri;
  // Gösterilecek bir şey yoksa şerit AÇILMAZ — tıklayınca boş bir alan açan
  // akordiyon, çalışmayan düğmedir. (Ölçüt akordiyonun GERÇEK içeriğidir;
  // eskiden asılı çiplerle ortak bir liste ölçülüyordu → isimler yukarı taşınmış
  // olsa bile şerit "açılabilir" görünüp boş açılırdı.)
  const seritAcilabilir =
    descKirpik || seritBilgiCipleri.length > 0 || akordiyonKisileri.length > 0;

  // FAZ-55/15: dört sonuç akışının TEK paneli. Hem yazılabilir sohbette (normal
  // dal) hem kilitli sohbette (iptal sonrası) AYNI değişken çizilir — iki ayrı
  // kopya olsaydı biri güncellenip öbürü unutulurdu.
  const gorevAkisPaneli =
    onayMode === null ? null : (
      <>
        {staged.length > 0 && stagedPreview}
        <WizardQuestion
          steps={akisAdimlari()}
          submitting={sending}
          onAttachFiles={can("mesajlasma.media.send") ? onPickFiles : undefined}
          hasAttachment={staged.length > 0}
          onComplete={akisTamamla}
          onCancel={() => {
            clearStaged();
            setOnayMode(null);
          }}
        />
      </>
    );

  const quickChips = !gorevVerAcik && !completeOpen && dueEdit === null && onayMode === null;
  // G11: atanan görevi henüz "yeni" iken — sohbete yazmak yerine önce işe başlamaya
  // TEŞVİK: belirgin bir bant + vurgulu "İşe Başladım". Yazmayı ENGELLEMEZ (serbest);
  // yalnız başlamayı ön plana çıkarır. Başlayınca G14 ile sohbete de mesaj düşer.
  const startNudge = startable && isDoer && taskStatus === "yeni";

  // D1: görsele tıkla → lightbox. (G12: üstteki sabit galeri şeridi kaldırıldı —
  // görseller yalnız kendi mesaj baloncuğunda; yukarıda topluca gösterilmez.)
  // FAZ-55/16: grup verilirse modalda ileri/geri gezinilir (çoklu ek).
  const onOpenImage = useCallback(
    (att, src, grup) => setLightbox({ att, src, grup: grup && grup.length > 1 ? grup : null }),
    []
  );
  // D2: mesaj türü — görev-bazlı / başvuru-bazlı / kişisel (renk + rozet).
  const msjType = (m) => (m.ref_task ? "gorev" : m.ref_application ? "basvuru" : "kisisel");

  // Başlığın altına SABİTLENEN bağlam şeridi: sohbette geçen görev/başvuru
  // referansları (tekilleştirilmiş). Konu görevi zaten üstteki zengin kartta →
  // şeritten düşülür. Görevler ve başvurular AYRI renkte (kullanıcı isteği).
  // FAZ-37: pinned şeritleri özellik-duyarlı — görev kapalıyken görev şeridi,
  // başvuru kapalıyken başvuru şeridi hiç oluşmaz (sıfır-iz).
  const pinnedTasks = useMemo(() => {
    // G2: görev sohbetinde üstteki zengin kart yeterli → mesaj-içi görev pin'i (SS-2) gösterme.
    if (!msjFeatures.gorev || topicTaskId) return [];
    const map = new Map();
    for (const m of messages || []) {
      if (m.ref_task && !map.has(m.ref_task.id)) map.set(m.ref_task.id, m.ref_task);
    }
    if (topicTaskId) map.delete(topicTaskId); // konu görevi = zengin kart
    // Yalnız AKTİF görevler sabit şeritte kalsın — tamamlanan/iptal düşer.
    const AKTIF = ["yeni", "devam_ediyor", "geri_bildirim_bekliyor"];
    return [...map.values()].filter((t) => !t.status || AKTIF.includes(t.status));
  }, [messages, topicTaskId, msjFeatures.gorev]);
  const pinnedApps = useMemo(() => {
    if (!msjFeatures.basvuru) return [];
    const map = new Map();
    for (const m of messages || []) {
      if (m.ref_application && !map.has(m.ref_application.id)) {
        map.set(m.ref_application.id, m.ref_application);
      }
    }
    return [...map.values()];
  }, [messages, msjFeatures.basvuru]);
  const pinDate = (d) =>
    d ? new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "";

  // "Sohbeti sil" = yalnız kendi görünümünden gizle (E1). Üyelik/görev korunur;
  // yeni mesajda geri gelir. Başarınca ebeveyni (dock/sayfa) bilgilendir.
  const doHide = async () => {
    setConfirmHide(false);
    try {
      await hideConversation(conversationId);
      toast.success("Sohbet kaldırıldı.");
      onHidden?.(conversationId);
    } catch {
      toast.error("Sohbet kaldırılamadı.");
    }
  };

  // FAZ-52: sohbete ad ver / adı sil. Boş kaydetmek adı siler → başlık yine
  // türetilmiş hâline (DM'de karşı tarafın adı, grupta tür adı) döner.
  const adKaydet = async () => {
    if (adKaydediliyor) return;
    setAdKaydediliyor(true);
    try {
      const data = await renameConversation(conversationId, adTaslak.trim());
      if (data?.title) {
        setTitle(data.title);
        setAdElle(!!adTaslak.trim());
        // Boşaltma "yansımaya dön" demek: backend adı konunun başlığına geri
        // yazar. Kutu boş kalırsa kullanıcı adın silindiğini sanır — dönen
        // gerçek değer yazılır.
        setAdTaslak(data.title);
      }
      // FAZ-114: `onTaskChanged` ÇAĞRILMAZ. FAZ-81'de bu satır vardı çünkü ad
      // değişimi talebi de değiştiriyordu ve sol sütunun tazelenmesi gerekiyordu.
      // Artık talep bu işlemden ETKİLENMİYOR; tazeleme, değişmemiş bir kaydı
      // yeniden çekmekten ve sohbeti gereksiz yere söktürmekten ibaret olurdu
      // (FAZ-80 dersi: değişmeyen dispatch bile yeniden çizim tetikliyor).
      toast.success(
        adTaslak.trim() ? "Sohbet adı güncellendi." : "Sohbet adı kaldırıldı."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sohbet adı değiştirilemedi.");
    } finally {
      setAdKaydediliyor(false);
    }
  };

  // FAZ-D: birimin saha personelini ekle — eklenenler yalnız o andan sonrasını
  // görür (backend visible_since). Tek birim varsa direkt ekler; çoksa seçici.
  const doAddSaha = async (unitId) => {
    setSahaUnits(null);
    try {
      const data = await addSahaPersonel(conversationId, unitId);
      if (data?.count > 0) {
        toast.success(`${data.count} saha personeli eklendi — bu andan itibaren sohbeti görürler.`);
      } else {
        toast.info?.("Eklenecek yeni saha personeli yok (zaten üyeler).");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Saha personeli eklenemedi.");
    }
  };

  const openSahaPicker = async () => {
    setSahaUnits([]); // yükleniyor
    try {
      const data = await searchContacts("");
      const units = data?.units || [];
      if (units.length === 0) {
        setSahaUnits(null);
        toast.error("Kapsamınızda eklenebilir birim yok.");
      } else if (units.length === 1) {
        await doAddSaha(units[0].id); // tek birim → direkt ekle
      } else {
        setSahaUnits(units); // seçici göster
      }
    } catch {
      setSahaUnits(null);
      toast.error("Birimler alınamadı.");
    }
  };

  // FAZ-81 — SOHBETE KİŞİ EKLE. Rehber (contacts) zaten kapsam + rütbe
  // süzgecinden geçmiş kişileri döner; burada yalnız ZATEN katılımcı olanlar
  // elenir. Backend ayrıca izni ve kapsamı yeniden doğrular (UI kapısı tek
  // başına yeterli sayılmaz).
  const openKisiEkle = async () => {
    setKisiEkle({ loading: true, users: [], q: "" });
    try {
      const data = await searchContacts("");
      const mevcut = new Set(participants.map((p) => p.id));
      setKisiEkle({
        loading: false,
        q: "",
        users: (data?.users || []).filter((u) => !mevcut.has(u.id)),
      });
    } catch {
      setKisiEkle(null);
      toast.error("Kişiler alınamadı.");
    }
  };

  const doAddParticipant = async (u) => {
    if (kisiEkleniyor) return;
    setKisiEkleniyor(u.id);
    try {
      const data = await addParticipant(conversationId, u.id);
      if (data?.already) {
        toast.info?.(`${u.display_name} zaten sohbette.`);
      } else {
        toast.success(
          `${u.display_name} eklendi — eklendiği andan önceki yazışmaları görmez.`
        );
      }
      setKisiEkle(null);
      pokeMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kişi eklenemedi.");
    } finally {
      setKisiEkleniyor(0);
    }
  };

  // FAZ-B: kurucu üyeyi gruptan çıkarır — çıkan sohbeti artık göremez.
  const doRemoveMember = async () => {
    const hedef = confirmRemove;
    setConfirmRemove(null);
    if (!hedef) return;
    try {
      await removeMember(conversationId, hedef.id);
      setParticipants((prev) => prev.filter((p) => p.id !== hedef.id));
      toast.success(`${hedef.name} gruptan çıkarıldı.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.user_id || "Üye çıkarılamadı.");
    }
  };

  // ── Mesaj satırları ────────────────────────────────────────────────
  // FAZ-66: konum panelinde otomatik GPS aranacak mı — telefon/dikey tablet.
  const mobilCihaz = tekPencereMi();

  const rows = [];
  let prevDay = "";
  const grup = kind === "grup" || kind === "gorev";
  for (const m of messages || []) {
    const dk = dayKey(m.created_at);
    if (dk && dk !== prevDay) {
      prevDay = dk;
      rows.push(<div key={`day-${dk}`} className="msj-day">{trDay(m.created_at)}</div>);
    }
    const mine = viewerId && Number(m.author) === viewerId;
    const tur = msjType(m); // D2: gorev | basvuru | kisisel
    const hasRef = !!(m.ref_task || m.ref_application);
    // Oto "📋 Görev oluşturuldu: …" gövdesi kartla mükerrer → gizle (kart konuyu taşır).
    const isAutoGorevMsg = m.ref_task && /^📋 Talep oluşturuldu:/.test(m.body || "");
    // Caption'sız ek yüklemede backend gövdeyi "📎 <dosyaadı>" yapıyor → gizle
    // (resim adları görünmesin; belge adı zaten dosya-hapında var).
    // FAZ-55/16: çoklu yüklemede gövde "📎 N dosya" olur — o da otomatiktir.
    const isAutoFileMsg =
      (m.attachments || []).some((a) => (m.body || "") === `📎 ${a.filename}`) ||
      /^📎 \d+ dosya$/.test(m.body || "");
    // FAZ-66: yazısız konum gönderiminde backend gövdeyi "📍 Konum" yapar
    // (sohbet listesi önizlemesi anlamlı kalsın diye). Baloncukta haritanın
    // ÜSTÜNDE ayrıca bu yazıyı göstermek mükerrer olurdu.
    const isAutoKonumMsg = !!m.konum && (m.body || "") === "📍 Konum";
    // FAZ-49: silinen mesaj satırı KALIR — gövde/ek/tepki yerine sade bir iz.
    if (m.is_deleted) {
      rows.push(
        <div key={m.id} data-mid={m.id} className={`msj-row ${mine ? "mine" : ""}`}>
          {!mine && grup && (
            <span className="msj-row-avatar">
              <Avatar name={m.author_display} src={m.author_avatar} size={28} />
            </span>
          )}
          <div className="msj-bubble msj-bubble--deleted">
            <em>Bu mesaj silindi</em>
            <div className="msj-meta"><span>{trTime(m.created_at)}</span></div>
          </div>
        </div>
      );
      continue;
    }
    rows.push(
      <div key={m.id} data-mid={m.id} className={`msj-row ${mine ? "mine" : ""}`}>
        {!mine && grup && (
          <span className="msj-row-avatar">
            <Avatar name={m.author_display} src={m.author_avatar} size={28} />
          </span>
        )}
        {/* FAZ-55/15: `tone` — görevin SONUCUNU taşıyan sistem mesajı. Onay
            yeşil, iptal kırmızı (kullanıcı isteği): sohbetin son sözü ne olduğu
            metni okumadan anlaşılsın. */}
        <div className={`msj-bubble ${tur !== "kisisel" ? `msj-bubble--${tur}` : ""} ${m.tone ? `msj-bubble--tone-${m.tone}` : ""} ${SONUC_TONLARI[m.tone] ? "has-teslimkart" : ""} ${hasRef ? "has-refmsg" : ""}`}>
          {!mine && grup && <div className="msj-who">{m.author_display || "—"}</div>}

          {m.ref_task && (
            <TaskMessageCard
              task={m.ref_task}
              onOpen={() => go(`/yonetim/gorevler/${m.ref_task.id}`)}
              /* FAZ-55/19: görev eklerinin resimleri de sohbetin lightbox'ında
                 açılır (kaydırmalı) — ayrı bir modal kurulmasın. */
              onOpenImage={onOpenImage}
            />
          )}
          {m.ref_application && (
            <AppMessageCard
              app={m.ref_application}
              canOpen={can("applications.view")}
              onOpen={() => go(`/yonetim/basvurular/${m.ref_application.id}`)}
            />
          )}

          {/* FAZ-55/16 — TESLİM KARTI. Kullanıcı: "onaya gönderilen mesaj tek
              blok olsun: en üstte Görev + Onay bekleniyor, altında ONAYA GÖNDEREN
              kişi (çok kişili grupta kimin gönderdiği anlaşılsın), altında zorunlu
              açıklama, en altta varsa resim/belgeler." Görev kartının altın dilinin
              MOR karşılığı — statü rengiyle aynı (onay bekleyen = mor). */}
          {SONUC_TONLARI[m.tone] ? (
            <div className={`msj-teslim-kart msj-teslim-kart--${m.tone}`}>
              {/* FAZ-55/18: "=" KALKTI (kullanıcı: "görev eşittir … gibi
                  şeyleri kaldır; orada görev yazsın, durum çip içinde olsun").
                  Görev kartıyla aynı dil: solda "GÖREV" etiketi, yanında durum
                  çipi. */}
              <div className="msj-teslim-kart-ust">
                <TaskIcon />
                <b>Talep</b>
                <span className="msj-teslim-rozet">{SONUC_TONLARI[m.tone].durum}</span>
              </div>
              <div className="msj-teslim-kart-kim">
                {SONUC_TONLARI[m.tone].kim}: <b>{m.author_display || "—"}</b>
              </div>
              {m.body && !isAutoFileMsg && (
                <div className="msj-teslim-kart-satir">
                  <span className="msj-teslim-kart-etiket">
                    {SONUC_TONLARI[m.tone].notEtiket}
                  </span>
                  <span className="msj-teslim-kart-not">{m.body}</span>
                </div>
              )}
              {/* FAZ-55/18: düzeltmeyle BİRLİKTE verilen ek süre AYRI mesaj
                  değil, aynı kartın satırı (kullanıcı: "o düzeltme kartının
                  içinde olsun"). */}
              {m.tone_extra && (
                <div className="msj-teslim-kart-satir">
                  <span className="msj-teslim-kart-etiket">Süre</span>
                  <span className="msj-teslim-kart-not">{m.tone_extra}</span>
                </div>
              )}
              <AttachmentGrid atts={m.attachments} onOpenImage={onOpenImage} />
            </div>
          ) : (
            <>
              {m.body && !isAutoGorevMsg && !isAutoFileMsg && !isAutoKonumMsg && (
                <div>{m.body}</div>
              )}
              {/* FAZ-66 — PAYLAŞILAN KONUM. Görev kartındaki harita bloğunun
                  TIPATIP aynısı (KonumBlok ortak modülü): mini harita, yol
                  tarifi ve tam ekran. Yazı varsa haritanın üstünde kalır —
                  "şurada buluşalım" + harita tek baloncuk. */}
              {m.konum && (
                <KonumBlok enlem={m.konum.lat} boylam={m.konum.lng} />
              )}
              <AttachmentGrid atts={m.attachments} onOpenImage={onOpenImage} />
            </>
          )}


          <div className="msj-meta">
            <span>{trTime(m.created_at)}</span>
            {mine && <Tick createdAt={m.created_at} receipts={receipts} />}
          </div>

          {/* MESAJ ARAÇLARI — iki köşe, ikisi de SABİT.
              FAZ-55/14 (kullanıcı: "zaten mesaja dahil bir üç nokta olsun,
              mesajın üzerinde hep sabit kalsın, hover'la açılmasın"):
              üç nokta artık baloncuğun İÇİNDE, sağ üstte, beyaz kutu/çerçeve
              olmadan duruyor — mesajın bir parçası. Menü: kopyala (herkes) +
              sil (yalnız kendi mesajın ve yetki varsa).
              ALT köşe → emoji düğmesi ve seçici AYNI sarmalayıcıda; seçici
              düğmeye göre konumlanır ve tek sıradır (width:max-content), bu
              yüzden mesaj kısaldıkça ne kayar ne de alt alta sarar. */}
          <div className={`msj-msgtools msj-msgmenu-wrap ${msgMenuFor === m.id ? "is-open" : ""}`}>
            <button
              type="button"
              className="msj-msgmenu-btn"
              onClick={() => mesajMenusu(m.id)}
              title="Mesaj menüsü"
              aria-label="Mesaj menüsü"
            >
              <EllipsisVerticalIcon />
            </button>
            {msgMenuFor === m.id && (
              <div className="msj-msgmenu" role="menu">
                <button type="button" role="menuitem" onClick={() => copyMsg(m)}>
                  <DocumentDuplicateIcon /> Mesajı kopyala
                </button>
                {mine && canDeleteMsg && (
                  <button
                    type="button"
                    role="menuitem"
                    className="is-danger"
                    onClick={() => {
                      setMsgMenuFor(null);
                      setDeleteFor(m.id);
                    }}
                  >
                    <TrashIcon /> Mesajı sil
                  </button>
                )}
              </div>
            )}
          </div>
          {/* FAZ-67: EMOJİ TEPKİLERİ EKRANDAN KALDIRILDI (kullanıcı isteği
              2026-07-29: "emojileri tamamen kaldıralım, sonra bakacağız").
              Uç, katalog ve veritabanı DURUYOR — bu bir söküm değil, yüzeyin
              kapatılması; geri açmak bu bloğu geri koymaktır. */}
        </div>
      </div>
    );
  }

  // Başlık avatarı
  const headAvatar = grup ? (
    <AvatarGroup people={participants} size={30} />
  ) : (
    <Avatar name={title} src={participants.find((p) => !p.is_self)?.avatar} size={34} />
  );
  // FAZ-55: başlık altı kişi listesi DÜZENLİ olsun — eskiden bütün katılımcılar
  // virgülle diziliyordu, kalabalık sohbette başlık satırı okunmaz hâle geliyordu.
  // Kural: "Siz" HER ZAMAN başta, sonra en çok 2 isim, gerisi "+N kişi".
  const grupOzeti = () => {
    const digerleri = participants.filter((p) => !p.is_self).map((p) => p.display_name);
    const ben = participants.some((p) => p.is_self) ? ["Siz"] : [];
    const gorunen = digerleri.slice(0, 2);
    const kalan = digerleri.length - gorunen.length;
    const parcalar = [...ben, ...gorunen];
    if (kalan > 0) parcalar.push(`+${kalan} kişi`);
    return parcalar.join(" · ");
  };
  const headSub = typing.length > 0 ? (
    <span className="msj-typing-inline">{typing.join(", ")} yazıyor…</span>
  ) : grup ? (
    grupOzeti()
  ) : (
    "Birebir sohbet"
  );

  return (
    <>
      {/* FAZ-74: ORTAK GİZLİ DOSYA GİRİŞİ KALDIRILDI.
          FAZ-F/FAZ-55/17'de burada koşulsuz mount edilen tek bir gizli
          <input type=file> vardı ve bütün ek düğmeleri onu `.click()` ile
          açıyordu. O günkü gerekçe (izin listesi eskiyince düğme sessizce
          ölmesin) hâlâ geçerli — ama çözümü YANLIŞ katmandaydı: asıl sessiz
          ölüm, JS ile açılan dosya penceresinin tarayıcı tarafından
          yutulmasıydı. Artık her ek yüzeyi (attach menüsü, EkSecici, sihirbaz
          composer ataçı) kendi <label>'ının içinde kendi girişini taşıyor;
          giriş koşulsuz mount edilmeye devam ediyor, tetikleyici ise JS değil
          tarayıcının kendi etiket→giriş etkinleştirmesi. */}
      <div className="msj-thread-head">
        {variant !== "task" && (
          <button type="button" className="msj-back" onClick={onBack} aria-label="Geri">
            <ArrowLeftIcon />
          </button>
        )}
        {headAvatar}
        {/* Başlığa tıkla → katılımcı + sohbet menüsü paneli (task varyantı hariç) */}
        <div
          className={`msj-thread-head-main ${variant !== "task" ? "msj-thread-head-main--click" : ""}`}
          onClick={variant !== "task" ? () => { setAdTaslak(title || ""); setMembersOpen(true); } : undefined}
          title={variant !== "task" ? "Katılımcılar ve sohbet ayarları" : undefined}
        >
          <div className="msj-thread-title">{gorevSohbetBaslik()}</div>
          <div className="msj-thread-sub">{headSub}</div>
        </div>
        {/* Görev detayı ikonu başlıktan KALDIRILDI (kullanıcı isteği) — detaya
            erişim sabit görev şeridi/kartından. */}
        {/* FAZ-65: üç nokta menüsü GÖREV DETAYI sohbetinde de var (kullanıcı
            isteği: "bunu görev detayındaki sayfadaki sohbet penceresinde de
            istiyorum"). Panelin kendisi zaten task varyantına hazırdı — yalnız
            girişi kapalıydı; katılımcılar, saha ekleme, sohbet adı ve ses
            anahtarı görev sohbetinde de anlamlı. Panelin İÇİNDEKİ "Sohbeti sil"
            görev varyantında görünmemeye devam eder (görevin sohbeti göreve
            aittir, tek başına gizlenemez). */}
        <button type="button" className="msj-thbtn" onClick={() => { setAdTaslak(title || ""); setMembersOpen(true); }} title="Sohbet menüsü" aria-label="Sohbet menüsü">
          <EllipsisVerticalIcon />
        </button>
      </div>

      {/* FAZ-55 — GÖREV ŞERİDİ (eski "zengin kart"ın yerine).
          Kullanıcı kuralı: sohbetin üstünde SABİT, İNCE, TEK SATIR bir şerit.
          Satır düzeni (soldan sağa, sabit): ATAYAN → DURUM → SÜRE → TARİH.
          Altında TEK SATIRLIK açıklama (asla sarmaz — pencere ne kadar dar
          olursa olsun sığan kadarı görünür, gerisi "…").
          Sağda dikey çizgiyle AYRILMIŞ dikdörtgen "Detay" alanı → görev
          sayfasına gider. Şeridin GERİ KALANINA tıklamak şeridi AŞAĞI DOĞRU
          genişletir (tam açıklama, atananlar, adres…). */}
      {taskDetail && variant !== "task" && (
        <div className={`msj-serit ${statusTone(taskDetail)} ${seritAcik ? "is-acik" : ""}`}>
          <div className="msj-serit-row">
            <button
              type="button"
              className={`msj-serit-body ${seritAcilabilir ? "" : "is-pasif"}`}
              onClick={seritAcilabilir ? () => setSeritAcik((v) => !v) : undefined}
              aria-expanded={seritAcilabilir ? seritAcik : undefined}
              disabled={!seritAcilabilir}
              title={
                seritAcilabilir ? (seritAcik ? "Şeridi daralt" : "Ayrıntıyı aç") : undefined
              }
            >
              {/* FAZ-55/2 — NİZAM: satırdaki HER öğe aynı chip ailesinden, sıra
                  SABİT: Atayan → Durum → Süre → Verildi → Son tarih.
                  (Önceki sürümde atayan kalın metin, tarih soluk metindi; tek
                  satırda dört ayrı görsel dil vardı — kullanıcı haklı olarak
                  "nizami değil" dedi.) */}
              <span className="msj-serit-line">
                {/* 1) ATAYAN — ad + minik profil (kullanıcı: "yanına minik bir
                    profil fotoğrafı koyabilirsin"). Avatar fotoğraf yoksa baş
                    harflere düşer → çip her hâlükârda aynı boyda kalır. */}
                <span className="msj-serit-chip is-kisi" title={`Veren: ${taskDetail.created_by_display || "—"}`}>
                  <Avatar
                    name={taskDetail.created_by_display || "—"}
                    src={taskDetail.created_by_avatar}
                    size={14}
                  />
                  {/* Ad kendi kutusunda — flex kapsayıcıda çıplak metne
                      text-overflow işlemiyor (ad "…"siz kesiliyordu). */}
                  <span className="msj-serit-ad">{taskDetail.created_by_display || "—"}</span>
                </span>
                {/* 2) DURUM — kullanıcının dilinde (Başlandı / Kapandı …).
                    Rengi şeridin TONUNDAN gelir (st-- değişkenleri) → çip ile
                    şerit hiçbir zaman ayrı renk söylemez. */}
                <span className="msj-serit-chip is-durum">{statusLabel(taskDetail)}</span>
                {/* öncelik yalnız normal-dışıysa (nizamı bozmasın) */}
                {/* FAZ-55/6: çıplak "Yüksek" ne dediği belli olmuyordu →
                    kullanıcı: "aciliyet = yüksek gibi bir şey yapalım."
                    Etiket çipin içinde, soluk; değer kalın. */}
                {taskDetail.priority && taskDetail.priority !== "normal" && (
                  <span className={`msj-serit-chip is-oncelik gk-priority--${taskDetail.priority}`}>
                    <span className="msj-serit-etiket">Aciliyet</span>
                    {taskDetail.priority_display}
                  </span>
                )}
                {/* 3) VERİLDİ — görevin açılış tarihi */}
                {taskDetail.created_at && (
                  <span className="msj-serit-chip is-tarih" title="Talebin verildiği tarih">
                    Verildi {trDate(taskDetail.created_at)}
                  </span>
                )}
                {/* 4) SON — termin tarihi (+ saat varsa) */}
                {taskDetail.due_date && (
                  <span className="msj-serit-chip is-tarih" title="Son teslim tarihi">
                    Son {trDate(`${taskDetail.due_date}T00:00:00`)}
                    {taskDetail.due_time ? ` · ${String(taskDetail.due_time).slice(0, 5)}` : ""}
                  </span>
                )}
                {/* 5) KALAN/GECİKEN — "Son"un hemen ardında, onu niteler
                    (gecikmede kırmızıya döner; termin baskısının tek işareti). */}
                {dueText(taskDetail) && (
                  <span className={`msj-serit-chip is-sure ${stripClass(taskDetail)}`}>
                    <ClockIcon className="msj-serit-ic" />
                    {dueText(taskDetail)}
                  </span>
                )}
              </span>
              {/* Çiplerin ALTINDA tek satır: detaylıda başlık (+ sığarsa
                  açıklama), hızlıda yalnız açıklama. Asla sarmaz.
                  FAZ-55/9 (kullanıcı, 2026-07-28): "açılan akordiyon yapısında
                  ayrı bir açıklama var, her iki açıklama da orada … açılınca
                  yukarıdan açıklama gitsin, sadece aşağıda olsun."
                  → ŞERİT AÇIKKEN açıklama BURADAN kalkar; tamamı akordiyonda
                  bir kez okunur. Başlık üstte kalır (o tekrar etmiyor). */}
              {(seritMetin.baslik || (seritMetin.aciklama && !seritAcik)) && (
                <span className="msj-serit-desc" ref={seritDescRef}>
                  {seritMetin.baslik && (
                    <b className="msj-serit-desc-baslik">{seritMetin.baslik}</b>
                  )}
                  {seritMetin.aciklama && !seritAcik && (
                    <span className="msj-serit-desc-metin">{seritMetin.aciklama}</span>
                  )}
                </span>
              )}
            </button>
            {/* Dikey çizgiyle ayrılmış dikdörtgen detay alanı */}
            <button
              type="button"
              className="msj-serit-detay"
              onClick={() => go(`/yonetim/gorevler/${taskId}`)}
              title="Talep detay sayfasına git"
            >
              Detay <span aria-hidden="true">→</span>
            </button>
          </div>

          {/* FAZ-55/6 — AKORDİYON, ŞERİDİN DEVAMI (ayrı bir alan DEĞİL).
              Kullanıcı düzeltmesi (2026-07-27), birebir:
                • "şerit hâlindeyken zaten açıklamanın tamamını görüyorsa
                   [akordiyonda] daha şeyi görmeyelim" → tam metin YALNIZ
                   üstteki satır gerçekten kırpıldıysa (descKirpik) çizilir.
                • "nereye dokunursam kapansın" → açılan alanın herhangi bir
                   yerine dokunmak şeridi kapatır; yalnız kişi çipi kendi
                   menüsünü açar (stopPropagation).
                • "atayan kişiyi [akordiyonda] görmeyelim" → kişi çipleri
                   atayanı ve kullanıcının kendisini içermez (seritKisileri).
                • "üzerlerine tıklayınca ne durumdayız, fotoğraf paylaşır mısın
                   gibi sorular çıkacak" → çipler tıklanabilir; eskiden şeridin
                   ALTINDA ayrı bir sıra olan bu çipler buraya taşındı. */}
          {seritAcik && seritAcilabilir && (
            <div
              className="msj-serit-acik"
              onClick={() => setSeritAcik(false)}
              role="presentation"
            >
              {/* FAZ-55/9: açıklama artık yukarıda DEĞİL, yalnız burada — tam
                  hâliyle, bir kez. (Önceki sürümde üstte kırpılmış hâli, altta
                  tam hâli aynı anda duruyordu; kullanıcı haklı olarak "baştan
                  okumayalım" dedi.) */}
              {taskDetail.description && (
                <p className="msj-serit-tamdesc">{taskDetail.description}</p>
              )}
              {seritBilgiCipleri.length > 0 && (
                <div className="msj-serit-cipler">
                  {seritBilgiCipleri.map((c) => (
                    <span key={c.k} className={`msj-serit-chip is-bilgi is-${c.k}`}>
                      {c.metin}
                    </span>
                  ))}
                </div>
              )}
              {/* FAZ-55/16: akordiyondaki katılımcılar artık YALNIZ BİLGİ —
                  tıklanamaz, menü açmaz. Kullanıcı: "akordiyonu açınca tüm
                  katılımcıları görelim ama adı tıklanabilir olmayacak, sadece
                  isim olarak görünecek." Tıklanabilir çipler şeridin ALTINA,
                  asılı sıraya taşındı (aşağıdaki .msj-serit-kisiler). */}
              {akordiyonKisileri.length > 0 && (
                <div className="msj-serit-cipler">
                  {akordiyonKisileri.map((k) => (
                    <span key={`sa-${k.id}`} className="msj-serit-chip is-kisi is-dumduz">
                      <Avatar name={k.name} src={k.avatar} size={14} />
                      <span className="msj-serit-ad">{k.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FAZ-55/16 — ASILI KATILIMCI ÇİPLERİ (yalnız ATAYAN görür).
              Kullanıcı: "başkan A,B,C,D,E kişilerine görev attıysa hepsini
              akordiyona tıklamadan şeridin altında görsün; üzerine tıklayınca
              'ne durumdayız / fotoğraf gönderebilir misin' soruları çıksın ve
              o kişinin adına mesaj gitsin. Tek atananlı görevde gerek yok —
              muhatap zaten belli."
              Kapı: canAsk (backend can_ask_status + atayan olmak) VE en az iki
              atanan. Atanan taraf bu şeridi HİÇ görmez. */}
          {asiliKisiler.length > 0 && (
            <div className="msj-serit-kisiler">
              {asiliKisiler.map((k) => (
                <span key={`sk-${k.id}`} className="msj-person-chip-wrap">
                  <button
                    type="button"
                    className={`msj-serit-chip is-kisi msj-person-chip ${personMenuFor === k.id ? "is-acik" : ""}`}
                    title={`${k.name} — soru gönder`}
                    onClick={() => setPersonMenuFor(personMenuFor === k.id ? null : k.id)}
                  >
                    <Avatar name={k.name} src={k.avatar} size={14} />
                    <span className="msj-serit-ad">{k.name}</span>
                  </button>
                  {personMenuFor === k.id && (
                    <div className="msj-person-menu" role="menu">
                      {KISI_SORULARI.map((so) => (
                        <button
                          key={so.key}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setPersonMenuFor(null);
                            sendPreset(so.metin(k.name));
                          }}
                        >
                          {so.label}
                        </button>
                      ))}
                    </div>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Başlık altına SABİTLENEN görev/başvuru şeridi — görev (lacivert) ve
          başvuru (altın) AYRI renkte; uzun başlık kısalır. */}
      {variant !== "task" && (pinnedTasks.length > 0 || pinnedApps.length > 0) && (
        <div className="msj-pins">
          {pinnedTasks.map((t) => (
            <button
              key={`pt-${t.id}`}
              type="button"
              className="msj-pin msj-pin--gorev"
              onClick={() => go(`/yonetim/gorevler/${t.id}`)}
              title="Talebe git"
            >
              <TaskIcon />
              <span className="msj-pin-body">
                <span className="msj-pin-title">{t.title || `Talep #${t.id}`}</span>
                <span className="msj-pin-sub">
                  <b>Talep</b>
                  {t.status_display && <span>{t.status_display}</span>}
                  {t.due_date && <span>Süre: {pinDate(t.due_date)}</span>}
                  {t.assigned_by && <span>Veren: {t.assigned_by}</span>}
                </span>
              </span>
              <span className="msj-pin-go">Detay →</span>
            </button>
          ))}
          {pinnedApps.map((a) => {
            const clickable = can("applications.view");
            return (
              <button
                key={`pa-${a.id}`}
                type="button"
                className="msj-pin msj-pin--basvuru"
                disabled={!clickable}
                onClick={clickable ? () => go(`/yonetim/basvurular/${a.id}`) : undefined}
                title={clickable ? "Başvuruya git" : undefined}
              >
                <LinkIcon />
                <span className="msj-pin-body">
                  <span className="msj-pin-title">#{a.tracking_number}{a.subject ? ` · ${a.subject}` : ""}</span>
                  <span className="msj-pin-sub">
                    <b>Başvuru</b>
                    {a.status_display && <span>{a.status_display}</span>}
                  </span>
                </span>
                {clickable && <span className="msj-pin-go">Detay →</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className={`msj-msgs ${taskId ? "msj-msgs--gorev" : ""}`} ref={scrollRef} onScroll={onScroll}>
        {loadingOlder && <div className="msj-day">Eski mesajlar yükleniyor…</div>}
        {messages === null && <div className="msj-empty">Mesajlar yükleniyor…</div>}
        {messages !== null && rows.length === 0 && (
          <div className="msj-empty">Henüz mesaj yok — ilk mesajı siz yazın.</div>
        )}
        {rows}
        {typing.length > 0 && (
          <div className="msj-typing-row">
            <div className="msj-typing-bubble">
              <span className="msj-typing-dot" />
              <span className="msj-typing-dot" />
              <span className="msj-typing-dot" />
            </div>
          </div>
        )}
      </div>

      {/* FAZ-37: writable=false → composer + attach + aksiyon menüsü + görev
          hızlı-chip satırı HİÇ render edilmez; yerine kilit bandı — proaktif
          gizleme (POST denemesi yok, 403 banner'ı DEĞİL). */}
      {lockState.writable === false ? (
        onayMode !== null ? (
          /* FAZ-55/15: İPTAL EDİLMİŞ görevin sohbeti kilitli — ama görevi VEREN
             hâlâ "yeniden aç / düzeltme iste" diyebilmeli. Bu yüzden kilit
             bandının yerine sihirbaz çizilir (kilit bandı + sihirbaz aynı anda
             durursa yine iki alan görünürdü). */
          gorevAkisPaneli
        ) : taskFlowOpen ? (
          /* FAZ-38: biten görev bandından yeni görev — kilitli sohbette de
             TaskComposer açılır; onDone conversation_id'yi yerinde-geçişe verir. */
          <TaskComposer
            mode={taskFlowOpen}
            conversationId={conversationId}
            targets={assignableOthers}
            onCancel={() => setTaskFlowOpen(false)}
            onDone={handleTaskComposerDone}
          />
        ) : (
          <div className="msj-lockbar" role="status">
            <LockClosedIcon width={16} />
            <span>{LOCK_MESAJLARI[lockState.lockedReason] || "Bu sohbet salt-okunur."}</span>
            {/* FAZ-38: görev tamamlandı bandında yeni-görev chip'leri (atama yetkilisi
                + alt-rütbeli hedef varsa) → "sanki aynı kişiye yeni görev" akışı. */}
            {lockState.lockedReason === "gorev_kapali" && (canYenidenGorev || canReopen) && (
              <div className="msj-lockbar-chips">
                {/* FAZ-55/15: İPTALİ GERİ AL. Kullanıcı: "görevi iptal ettikten
                    sonra bir daha geri alma isteği oluşabileceği için geri çekme
                    seçeneği… yanında bir de düzeltme iste butonu olsun; ikisinden
                    biri çalıştırılırsa aynı görevde ne durumdayız / süre güncelle
                    / iptal et çipleri geri gelsin."
                    Yalnız `can_reopen` bayrağıyla çizilir (backend: tasks.reopen
                    + görevi VEREN taraf) — atanan bu düğmeleri GÖRMEZ. */}
                {canReopen && (
                  <>
                    <button type="button" className="msj-lock-chip is-primary" onClick={() => setOnayMode("yeniden")}>
                      {taskStatus === "iptal_edildi" ? "İptali geri al" : "Yeniden aç"}
                    </button>
                    <button type="button" className="msj-lock-chip" onClick={() => setOnayMode("yeniden_duzeltme")}>
                      Düzeltme iste
                    </button>
                  </>
                )}
                {canYenidenGorev && (
                  <>
                    <button type="button" className="msj-lock-chip is-hizli" onClick={() => setTaskFlowOpen("hizli")}>
                      Yeni Hızlı Talep
                    </button>
                    <button type="button" className="msj-lock-chip" onClick={() => setTaskFlowOpen("detayli")}>
                      Yeni Talep Gönder
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )
      ) : (
      <>
      {/* C1: Adım-adım görev composer'ı (form değil — mesaj yazma alanında) */}
      {taskFlowOpen && (
        <TaskComposer
          mode={taskFlowOpen}
          conversationId={conversationId}
          targets={assignableOthers}
          onCancel={() => setTaskFlowOpen(false)}
          onDone={handleTaskComposerDone}
        />
      )}

      {/* FAZ-55/15: ATAYAN'ın sonuç akışları (onayla-kapat / düzeltme iste /
          iptal et / yeniden aç) — sohbetin içinde, tek mesaj kutusuyla. */}
      {gorevAkisPaneli}

      {/* G13: Görevi tamamla — AYRI FORM DEĞİL, mesaj kısmıyla BİRLEŞİK sihirbaz.
          (1) Kapanış notu (zorunlu) alttaki mesaj kutusundan yazılır → Gönder.
          (2) İkinci soru: fotoğraf ekle (isteğe bağlı) — foto ÖNCE yüklenir
          (tamamlanınca sohbet kilitlenir → sonrasında ek yüklenemez), sonra
          complete çağrısı notu düşürür + görevi kapatır (sohbet biter). */}
      {completeOpen && (
        <>
          {staged.length > 0 && stagedPreview}
          <WizardQuestion
            steps={[
              {
                key: "note",
                kind: "text",
                title: "İşi onaya gönder",
                placeholder: "Yapılan işi kısaca yazın (zorunlu)…",
                required: true,
                maxLength: 2000,
              },
              {
                // FAZ-55/17 (kullanıcı): "onaya gönderirken fotoğraf ekleme
                // sistemi, görev eklerkenki yapının AYNISI olsun." Seçenek
                // listesi kalktı; görev sihirbazındaki `EkSecici` bileşeninin
                // KENDİSİ kullanılıyor — iki ayrı ek deneyimi olmasın diye
                // kopyalanmadı, aynı bileşen çağrıldı.
                key: "foto",
                kind: "picker",
                title: "Fotoğraf veya belge",
                render: ({ onDone, onSkip }) => (
                  <EkSecici
                    adet={staged.length}
                    onDosya={onPickFiles}
                    onDone={() => onDone({})}
                    onSkip={onSkip}
                  />
                ),
              },
            ]}
            submitting={sending}
            onComplete={(a) => {
              const note = (a.note || "").trim();
              setCompleteOpen(false);
              if (!note) return;
              // FAZ-55: son adım ONAY MODALI — yazılan metin + eklerin küçük
              // resimleri gösterilir, kullanıcı onaylayınca gönderilir.
              setTeslimOnay({ note });
            }}
            onCancel={() => {
              clearStaged();
              setCompleteOpen(false);
            }}
          />
        </>
      )}

      {/* C2: Süre güncelle. FAZ-55: ayrı date+time kutuları yerine ortak
          DuePicker (görev açarken kullanılanın aynısı) — tek bir süre seçme
          deneyimi. FAZ-55/10: "Süreyi kaldır" ayrı bir düğme SATIRI değil,
          onay satırının içindeki çip; panelde iki ayrı mesaj kutusu varmış gibi
          duruyordu (kullanıcı: "iki tane mesaj kutucuğu var"). */}
      {dueEdit !== null && (
        <div className="msj-wizard">
          <div className="msj-wizard-head">
            <span className="msj-wizard-title">Süreyi Güncelle</span>
          </div>
          <DuePicker
            initialDate={dueEdit.date}
            initialTime={dueEdit.time}
            onDone={({ due_date, due_time }) => submitDueEdit(due_date, due_time)}
            onCancel={() => setDueEdit(null)}
            onRemove={taskDetail?.due_date ? () => submitDueEdit("", "") : undefined}
          />
        </div>
      )}

      {/* Hızlı aksiyon çipleri (bağlama göre) + kalıp cevap panelleri.
          C2: ATAYAN "Ne durumdayız?" SORAR ↔ ATANAN "Durum bildir" ile BİLDİRİR
          (ayrı setler). Paneller küçük pencerede TAŞMASIN → kaydırmalı. */}
      {quickChips && (
        <>
          {/* FAZ-45: kalıp-soru panelleri WizardQuestion seçenekli-soru bileşenine taşındı. */}
          {askOpen && (
            <WizardQuestion
              steps={[{
                key: "q", kind: "options", title: "Ne durumdayız?",
                options: PRESET_QUESTIONS.map((q) => ({ label: q, patch: { question: q } })),
                // FAZ-E: serbest soru alttaki mesaj kutusundan yazılır (Tamam yok).
                composer: { placeholder: "Kendi sorunu yaz…", toPatch: (v) => ({ question: v.trim() }) },
              }]}
              onComplete={(a) => { setAskOpen(false); if (a.question) sendPreset(a.question); }}
              onCancel={() => setAskOpen(false)}
            />
          )}
          {statusOpen && (
            <>
              {staged.length > 0 && stagedPreview}
              <WizardQuestion
                steps={[{
                  key: "q", kind: "options", title: "Durum bildir",
                  options: ASSIGNEE_PRESETS.map((s) => ({ label: s, patch: { question: s } })),
                  // FAZ-E: serbest bildirim alttaki mesaj kutusundan (Tamam yok).
                  // FAZ-F: attach ile foto — durum SOHBET MESAJI olarak gider.
                  composer: { placeholder: "Kendi bildirimini yaz…", attach: true, toPatch: (v) => ({ question: v.trim() }) },
                }]}
                onAttachFiles={can("mesajlasma.media.send") ? onPickFiles : undefined}
                hasAttachment={staged.length > 0}
                onComplete={(a) => {
                  setStatusOpen(false);
                  const t = (a.question || "").trim();
                  if (staged.length > 0) sendStatusWithMedia(t);
                  else if (t) sendAssigneePreset(t);
                }}
                onCancel={() => { clearStaged(); setStatusOpen(false); }}
              />
            </>
          )}
          {!askOpen &&
            !statusOpen &&
            (canAsk || canReport || canEditDue || canRequestDue || startable || completable ||
              canApprove || canRequestChanges) && (
            <>
            {/* G11: atanan henüz işe başlamadıysa — belirgin teşvik bandı (yazmayı
                engellemez; sadece işe başlamayı vurgular). Bant açıkken küçük çip gizli. */}
            {startNudge && (
              <div className="msj-startnudge" role="note">
                {/* FAZ-55/16 — BANT METNİ YENİDEN YAZILDI. Kullanıcı: "yanındaki
                    bilgilendirme yazısı çok saçma, tümüyle sil; çünkü başladıktan
                    sonra sayaç başlamıyor — başkan görevi gönderdikten sonra sayaç
                    zaten başlıyor." Doğru tespit: termin atama anında işliyor.
                    Yeni metin yalnız DOĞRU olanı söyler: başlamadan iş kapatılamaz
                    ve başlayınca sohbete "İşe başladım" mesajı düşer.
                    Hover'daki "İşe başlayınca ne olur?" baloncuğu da KALDIRILDI. */}
                <div className="msj-startnudge-text">
                  <PlayIcon />
                  <span>
                    <b>İşe başlayın</b> — işi ancak başladıktan sonra onaya
                    gönderip kapatabilirsiniz. Bastığınızda sohbete “İşe başladım”
                    mesajı düşer{grup ? " ve ekip görür." : "."}
                  </span>
                </div>
                <button
                  type="button"
                  className="msj-startnudge-btn"
                  onClick={() => doTaskAction("start", {})}
                >
                  <PlayIcon /> İşe Başladım
                </button>
              </div>
            )}
            <div className="msj-quick">
              {canReport && (
                <button type="button" className="msj-quick-chip is-primary" onClick={() => setStatusOpen(true)}>
                  <ChatBubbleLeftRightIcon /> Durum bildir
                </button>
              )}
              {canAsk && (
                <button type="button" className="msj-quick-chip" onClick={() => setAskOpen(true)}>
                  <QuestionMarkCircleIcon /> Ne durumdayız?
                </button>
              )}
              {startable && !startNudge && (
                <button type="button" className="msj-quick-chip" onClick={() => doTaskAction("start", {})}>
                  <PlayIcon /> İşe Başladım
                </button>
              )}
              {completable && (
                <button type="button" className="msj-quick-chip is-mor" onClick={() => setCompleteOpen(true)}>
                  <CheckCircleIcon /> Onaya Gönder
                </button>
              )}
              {canEditDue && (
                <button
                  type="button"
                  className="msj-quick-chip"
                  onClick={() =>
                    setDueEdit({
                      date: taskDetail?.due_date || "",
                      time: (taskDetail?.due_time || "").slice(0, 5),
                    })
                  }
                >
                  <ClockIcon /> Süre güncelle
                </button>
              )}
              {canRequestDue && (
                <button type="button" className="msj-quick-chip" onClick={requestExtension}>
                  <ClockIcon /> Ek süre iste
                </button>
              )}
              {/* FAZ-55: ATAYAN'ın onay çipleri — atanan işi onaya gönderince görünür. */}
              {canApprove && (
                <button type="button" className="msj-quick-chip is-yesil" onClick={() => setOnayMode("onay")}>
                  <CheckCircleIcon /> Onayla ve kapat
                </button>
              )}
              {canRequestChanges && (
                <button type="button" className="msj-quick-chip" onClick={() => setOnayMode("duzeltme")}>
                  <ArrowUturnLeftIcon /> Düzeltme iste
                </button>
              )}
              {/* FAZ-55/15: "Görevi iptal et" — atayanın üçüncü çipi (ne
                  durumdayız / süre güncelle / iptal et). Gerekçe isteğe bağlı;
                  yazılırsa sohbetin SON mesajı olarak KIRMIZI düşer. */}
              {canCancel && (
                <button type="button" className="msj-quick-chip is-danger" onClick={() => setOnayMode("iptal")}>
                  <XMarkIcon /> Talebi iptal et
                </button>
              )}
              {/* FAZ-55: "Görev detayı →" çipi KALDIRILDI — detaya erişim artık
                  yalnız üstteki görev şeridinin sağındaki Detay alanından
                  (kullanıcı isteği: "yukarıda şerit şeklinde verdik zaten"). */}
            </div>
            </>
          )}
        </>
      )}

      {/* C1: görev akışı açıkken normal composer gizli — TaskComposer kendi girişini taşır.
          FAZ-E: Ne durumdayız / Durum bildir açıkken de gizli — o WizardQuestion kendi
          mesaj kutusunu (serbest cevap) taşır; tek bir alt-giriş kalır. G13: Tamamla
          sihirbazı açıkken de gizli (o da kendi mesaj kutusunu taşır).
          FAZ-55/10: SÜRE GÜNCELLEME paneli de kendi onay satırını taşıyor —
          kullanıcı: "süre güncelle deyince şu görev atama, resim ekleme falan
          gitsin; iki tane mesaj alanı var." */}
      {/* FAZ-55/15: onayMode BURADA EKSİKTİ — "Onayla ve kapat" / "Düzeltme
          iste" açıkken sihirbazın kendi mesaj kutusu VE bu normal composer aynı
          anda çiziliyordu (kullanıcı: "iki tane mesaj kutusu açılıyor"). */}
      {/* FAZ-66 — KONUM GÖNDERME PANELİ. Görev sihirbazındaki harita adımının
          AYNISI (KonumSecici): kullanıcı "görev atarken çıkan harita alanı
          neyse aynı mantıkta olsun" dedi. Mobilde otomatik GPS (otoBul) —
          "WhatsApp mantığı"; masaüstünde kurum merkezinden açılır. Diğer
          sihirbazlar gibi açıkken normal mesaj kutusu gizlenir (tek giriş). */}
      {konumAcik && (
        <div className="msj-wizard">
          <div className="msj-wizard-head">
            <span className="msj-wizard-title">Konum Gönder</span>
            <button type="button" className="msj-wq-skip" onClick={() => setKonumAcik(false)}>
              Vazgeç
            </button>
          </div>
          <KonumSecici
            otoBul={mobilCihaz}
            gonderEtiketi="Konumu gönder"
            onDone={konumGonder}
          />
        </div>
      )}

      {!taskFlowOpen && !askOpen && !statusOpen && !completeOpen && dueEdit === null && onayMode === null && !konumAcik && (
      <>
        {/* D: seçilen ekler — küçük önizleme (resimlerde ad YOK), sil + yazı ekle */}
        {staged.length > 0 && stagedPreview}

        <div className="msj-composer">
          {/* FAZ-66 — ATAÇ ARTIK DOĞRUDAN DOSYA SEÇİCİ AÇMAZ, MENÜ AÇAR.
              Kullanıcı (2026-07-29): "ataç ikonuna tıklayınca açılan yapıda
              iki farklı seçenek olsun: konum gönder + fotoğraf veya belge
              gönder." Menü dili görev/başvuru menüsüyle BİREBİR aynı
              (msj-attach-menu) — sohbette iki ayrı menü dili olmasın. */}
          <div className="msj-attach-wrap">
            <button
              type="button"
              className={`msj-iconbtn ${composerMenu === "ek" ? "is-active" : ""}`}
              onClick={() => menuAcKapa("ek")}
              title="Fotoğraf, belge veya konum gönder"
              aria-label="Fotoğraf, belge veya konum gönder"
            >
              <PaperClipIcon />
            </button>
            {composerMenu === "ek" && (
              <div className="msj-attach-menu pop-ok" data-ok="alt" data-ok-x="sol">
                {/* FAZ-72/2 — DOSYA SEÇİCİYİ ARTIK JAVASCRIPT AÇMIYOR.
                    Eskiden burada `filesInputRef.current?.click()` vardı: dosya
                    penceresini JS TETİKLİYORDU. Tarayıcılar bu çağrıyı yalnız
                    "kullanıcı etkinliği" penceresi içinde kabul eder ve kabul
                    etmediklerinde SESSİZCE yutarlar — ne hata, ne pencere, ne
                    istek. Kullanıcının "tıklıyorum, hiçbir şey olmuyor,
                    göndermiyor" dediği ve sunucu kaydında HİÇBİR iz bırakmayan
                    davranışın açıklaması budur (kayıt: 22:00–22:04 arası
                    tarayıcıdan tek bir yükleme isteği çıkmamış).
                    Artık madde bir <label> ve dosya girişi ONUN İÇİNDE: pencereyi
                    tarayıcının KENDİSİ açar (yerel etiket→giriş etkinleştirmesi),
                    araya JS girmez.
                    FAZ-74 DÜZELTMESİ: bu not eskiden "sihirbazlardaki ortak gizli
                    giriş yerinde duruyor, orada tetikleyici gerçek bir düğme
                    olduğu için etkinlik penceresi hep geçerli" diyordu. YANLIŞTI:
                    kullanıcının tarayıcısı sihirbazlardan da TEK BİR ek isteği
                    göndermemişti. Ayrım "düğme mi, değil mi" değil — JS'in araya
                    girip girmediğidir. Bütün ek yüzeyleri <label>'a çevrildi. */}
                {can("mesajlasma.media.send") && (
                  <label className="msj-attach-menu-label">
                    {/* FAZ-78: `hidden` (display:none) KULLANILMAZ — etiketle
                        açılan dosya seçici, çizilmeyen bir girişte mobil
                        tarayıcılarda sessizce yutulur. Giriş DOM'da kalır,
                        yalnız görünmez olur (.msj-gizli-dosya).
                        Sıra da değişti: DOSYALAR ÖNCE OKUNUR, menü SONRA
                        kapanır — kapatma, girişi söken durum güncellemesidir. */}
                    <input
                      type="file"
                      className="msj-gizli-dosya"
                      multiple
                      onChange={(e) => { onPickFiles(e); setComposerMenu(null); }}
                    />
                    <PhotoIcon />
                    <span className="msj-attach-menu-txt">
                      Fotoğraf veya Belge
                      <small>Birden çoğu tek mesajda gider</small>
                    </span>
                  </label>
                )}
                {/* Konum göndermek MEDYA İZNİ İSTEMEZ: dosya yüklenmiyor,
                    mesajın kendi alanına iki koordinat yazılıyor. Yazma
                    hakkı olan konum da paylaşabilir. */}
                <button
                  type="button"
                  onClick={() => { setComposerMenu(null); setKonumAcik(true); }}
                >
                  <MapPinIcon />
                  <span className="msj-attach-menu-txt">
                    Konum Gönder
                    <small>{mobilCihaz ? "Konumun otomatik bulunur" : "Haritadan işaretle"}</small>
                  </span>
                </button>
              </div>
            )}
          </div>
          {/* Görev / Başvuru menüsü (eski bağlantı ikonu yerine) — 3 seçenek alt alta */}
          {(canGorevBaslat || canRefApp) && (
            <div className="msj-attach-wrap">
              <button
                type="button"
                className={`msj-iconbtn ${composerMenu === "aksiyon" ? "is-active" : ""}`}
                onClick={() => menuAcKapa("aksiyon")}
                title="Talep gönder / Başvuru oluştur"
                aria-label="Talep gönder / Başvuru oluştur"
              >
                <TaskIcon />
              </button>
              {composerMenu === "aksiyon" && (
                <div className="msj-attach-menu pop-ok" data-ok="alt" data-ok-x="sol">
                  {canGorevBaslat && (
                    <button type="button" onClick={() => { setComposerMenu(null); setTaskFlowOpen("hizli"); }}>
                      <BoltIcon />
                      <span className="msj-attach-menu-txt">
                        Yeni Hızlı Talep
                        {/* FAZ-72: vaat NET olsun — yeni görev BU sohbete eklenmez,
                            KENDİ sayfasında/sohbetinde açılır (bir sohbet = bir görev). */}
                        <small>Aynı kişilere — kendi sayfasında açılır</small>
                      </span>
                    </button>
                  )}
                  {canGorevBaslat && (
                    <button type="button" onClick={() => { setComposerMenu(null); setTaskFlowOpen("detayli"); }}>
                      <TaskIcon />
                      <span className="msj-attach-menu-txt">
                        Yeni Talep Gönder
                        <small>Aynı kişilere — kendi sayfasında açılır</small>
                      </span>
                    </button>
                  )}
                  {canRefApp && (
                    <button
                      type="button"
                      onClick={() => {
                        setComposerMenu(null);
                        setText((t) => (t.includes("#") ? t : `${t}#`));
                        composerRef.current?.focus();
                      }}
                    >
                      <LinkIcon /> Başvuru Oluştur
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <textarea
            ref={composerRef}
            rows={1}
            value={text}
            maxLength={5000}
            placeholder={staged.length > 0 ? "Bir açıklama ekleyin (isteğe bağlı)…" : "Mesaj yazın…"}
            onChange={onTextChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            type="button"
            className="msj-sendbtn"
            disabled={(!text.trim() && staged.length === 0) || sending}
            onClick={send}
            title="Gönder"
            aria-label="Gönder"
          >
            <PaperAirplaneIcon />
          </button>
        </div>
      </>
      )}
      </>
      )}

      {/* D1: görsel lightbox (büyüt + indir + kapat) */}
      {lightbox && (() => {
        const grup = lightbox.grup;
        const i = grup ? grup.findIndex((a) => a.id === lightbox.att.id) : -1;
        const git = (adim) =>
          setLightbox({ att: grup[(i + adim + grup.length) % grup.length], src: null, grup });
        return (
          <Lightbox
            att={lightbox.att}
            src={lightbox.src}
            sira={grup && i >= 0 ? `${i + 1}/${grup.length}` : ""}
            onPrev={grup && i >= 0 ? () => git(-1) : undefined}
            onNext={grup && i >= 0 ? () => git(1) : undefined}
            onClose={() => setLightbox(null)}
          />
        );
      })()}

      {/* Başlık 3-nokta → katılımcı + sohbet ayarları. Portal DEĞİL — sohbet
          alanının (WP kartı) İÇİNDEN, sağdan kayan drawer (tüm ekranı kaplamaz). */}
      {membersOpen && (
          <div className="msj-members" onClick={() => setMembersOpen(false)} role="dialog" aria-modal="true">
            <div className="msj-members-panel" onClick={(e) => e.stopPropagation()}>
              {/* FAZ-94 madde 16 — PENCERE ARTIK KÜNYELİ.
                  Kullanıcı: "buradaki 3 noktaya tıklayınca gelen sayfayı çok
                  minimal değil, daha uyumlu güzel bir şey yap."
                  Eski hâli düz bir "Katılımcılar (N)" satırıydı: pencere hangi
                  sohbete ait olduğunu SÖYLEMİYORDU — kullanıcı üç noktaya
                  bastıktan sonra bağlamı kaybediyordu. Yeni üst blok başlığın
                  kendisini taşıyor: yüzler + sohbet adı + "N kişi · tür".
                  Eylemler (ad değiştirme, saha ekleme, sil) AYNEN duruyor —
                  bu madde yalnız sunum. */}
              <div className="msj-members-head msj-members-head--kunye">
                <div className="msj-members-kunye">
                  <span className="msj-members-kunye-ava">{headAvatar}</span>
                  <span className="msj-members-kunye-metin">
                    <span className="msj-members-kunye-ad">{gorevSohbetBaslik()}</span>
                    <span className="msj-members-kunye-alt">
                      {participants.length} kişi
                      {kind === "gorev"
                        ? " · Talep"
                        : kind === "basvuru"
                        ? " · Başvuru"
                        : kind === "grup"
                        ? " · Grup"
                        : " · Birebir"}
                    </span>
                  </span>
                </div>
                <button type="button" className="msj-lightbox-btn" onClick={() => setMembersOpen(false)} aria-label="Kapat">
                  <XMarkIcon />
                </button>
              </div>

              <div className="msj-members-bolum-bas">
                <UserGroupIcon width={16} />
                Katılımcılar ({participants.length})
              </div>

              {/* FAZ-52 — SOHBET ADI. Kullanıcı kararı (2026-07-27): grup
                  kurarken ad verilmemişse ya da sonradan ayırt edici bir ad
                  gerekiyorsa buradan verilebilsin. Tür AYRIMI YOK: kişisel /
                  grup / görev / başvuru. Görev-başvuru sohbetinde yalnız
                  SOHBETİN adı değişir — görevin/başvurunun başlığı korunur.
                  Boş bırakıp kaydetmek adı siler (başlık türetilmişe döner).
                  FAZ-55/7 (2026-07-28): "sohbet adını sadece o görevi veya
                  sohbeti başlatan kişi değiştirebilsin, başkası değil." Ad
                  herkeste görünen ORTAK bir alan; kurucu olmayana kutuyu
                  göstermek "kaydet → 403" demek olurdu. Kapı UI'da da kapalı;
                  asıl doğrulama backend'de (ConversationRenameView).

                  FAZ-114 (2026-08-05): kapı artık İZNE de bağlı
                  (`mesajlasma.conversation.rename`) ve iki şart backend'de
                  birleştirilip TEK bayrak olarak geliyor (viewer_can_rename).
                  Etiket de değişti — burada değiştirilen şey TALEBİN adı değil,
                  SOHBETİN adıdır. Eski "Talep adı" etiketi, kutunun talebi
                  yeniden adlandırdığı izlenimini veriyordu ve FAZ-81'de
                  gerçekten öyle davranıyordu; o davranış kaldırıldı. */}
              {assignCtx.canRename && (
              <div className="msj-members-rename">
                <label className="msj-members-rename-lbl" htmlFor="msj-conv-ad">
                  Sohbet adı
                </label>
                <div className="msj-members-rename-row">
                  <input
                    id="msj-conv-ad"
                    type="text"
                    className="msj-new-search"
                    maxLength={120}
                    placeholder={
                      kind === "gorev"
                        ? "Sohbete ad verin (boş bırakılırsa talebin başlığı)"
                        : "Ad verin (boş bırakılırsa otomatik)"
                    }
                    value={adTaslak}
                    onChange={(e) => setAdTaslak(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && adKaydet()}
                  />
                  <button
                    type="button"
                    className="btn-md btn-md-primary"
                    onClick={adKaydet}
                    disabled={adKaydediliyor}
                  >
                    {adKaydediliyor ? "…" : "Kaydet"}
                  </button>
                </div>
                {/* FAZ-114: FAZ-81'in "ad her yerde değişir" cümlesi KALDIRILDI —
                    o cümle doğruydu ama istenmeyen davranışı anlatıyordu. Yeni
                    metin sınırı açıkça çizer, çünkü kullanıcının en çok merak
                    ettiği şey talebin adının da değişip değişmeyeceğidir. */}
                {kind === "gorev" && (
                  <small className="msj-members-saha-note">
                    Yalnız <b>sohbetin adı</b> değişir. Talebin <b>başlığı ve
                    açıklaması etkilenmez</b>; talep listesinde ve detayında
                    kendi adıyla görünmeye devam eder.
                  </small>
                )}
              </div>
              )}

              <div className="msj-members-list">
                {participants.map((p, i) => (
                  <div key={p.id ?? i} className="msj-members-item">
                    <Avatar name={p.display_name} src={p.avatar} size={32} />
                    <span className="msj-members-name">
                      {p.is_self ? `${p.display_name} (Siz)` : p.display_name}
                    </span>
                    {/* FAZ-B: üye çıkarma — YALNIZ grubu kuran, yalnız grup sohbeti */}
                    {assignCtx.isCreator && kind === "grup" && !p.is_self && (
                      <button
                        type="button"
                        className="msj-members-kick"
                        onClick={() => setConfirmRemove({ id: p.id, name: p.display_name })}
                        title="Gruptan çıkar"
                        aria-label={`${p.display_name} kişisini gruptan çıkar`}
                      >
                        <XMarkIcon />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* FAZ-81 — SOHBETE KİŞİ EKLE (kullanıcı isteği 2026-07-30:
                  "başkan yardımcısı kişisi izni varsa sohbete kişi
                  ekleyebilsin"). Sohbeti KURMUŞ olma şartı YOK — izin yeter;
                  kurucunun eski yolu (participant.manage) da korunur. Talep ve
                  grup sohbetinde çalışır; DM'ye üçüncü kişi eklenmez.
                  Kilitli (kapanmış/iptal) sohbette katılımcı listesi dondurulur. */}
              {(kind === "gorev" || kind === "grup") &&
                lockState.writable &&
                (can("mesajlasma.participant.add") ||
                  (assignCtx.isCreator && can("mesajlasma.participant.manage"))) && (
                  <div className="msj-members-add">
                    {kisiEkle === null ? (
                      <button
                        type="button"
                        className="msj-members-addsaha"
                        onClick={openKisiEkle}
                      >
                        <UserPlusIcon /> Kişi ekle
                      </button>
                    ) : (
                      <div className="msj-members-add-panel">
                        <input
                          type="text"
                          className="msj-new-search"
                          placeholder="Kişi ara"
                          value={kisiEkle.q}
                          autoFocus
                          onChange={(e) =>
                            setKisiEkle((s) => ({ ...s, q: e.target.value }))
                          }
                        />
                        <div className="msj-members-add-list">
                          {kisiEkle.loading && <small>Yükleniyor…</small>}
                          {!kisiEkle.loading &&
                            kisiEkle.users.filter((u) =>
                              (u.display_name || "")
                                .toLocaleLowerCase("tr")
                                .includes((kisiEkle.q || "").toLocaleLowerCase("tr"))
                            ).length === 0 && <small>Eklenebilecek kişi yok.</small>}
                          {/* FAZ-82: "Kişi ekle" listesi de rehberle AYNI
                              yerleşimi kullanır (kullanıcı: "buradaki bu
                              yazıları da kişi ekleme kısmına da getirelim,
                              birimlere ayrılma muhabbetini"). Yerleşim tek
                              kaynaktan gelir → iki liste ayrışamaz. */}
                          {!kisiEkle.loading &&
                            rehberBolumleri(
                              kisiEkle.users
                                .filter((u) =>
                                  (u.display_name || "")
                                    .toLocaleLowerCase("tr")
                                    .includes((kisiEkle.q || "").toLocaleLowerCase("tr"))
                                )
                                .map(rehberSatiri)
                            ).map((b) => (
                              <React.Fragment key={b.anahtar}>
                                {b.baslik && (
                                  <div className="msj-new-grup">{b.baslik}</div>
                                )}
                                {b.kisiler.map((u) => (
                                  <button
                                    key={u.id}
                                    type="button"
                                    className="msj-members-add-item"
                                    disabled={!!kisiEkleniyor}
                                    onClick={() =>
                                      doAddParticipant({
                                        id: u.id,
                                        display_name: u.label,
                                      })
                                    }
                                  >
                                    <Avatar name={u.label} src={u.avatar} size={26} />
                                    <span className="grow">{u.label}</span>
                                    {u.saha && (
                                      <span className="msj-new-rozet">Saha</span>
                                    )}
                                  </button>
                                ))}
                              </React.Fragment>
                            ))}
                        </div>
                        <button
                          type="button"
                          className="msj-wizard-chip"
                          onClick={() => setKisiEkle(null)}
                        >
                          Vazgeç
                        </button>
                      </div>
                    )}
                    <small className="msj-members-saha-note">
                      Eklenen kişi <b>eklendiği andan önceki yazışmaları görmez</b>;
                      yalnız talep atama, onaya gönderme gibi <b>olay kayıtlarını</b>
                      {" "}görür.
                    </small>
                  </div>
                )}

              {/* FAZ-D: birimin saha personelini ekle — yalnız kurucu + grup +
                  üyelik/birim izinleri. Eklenen yalnız o andan sonrasını görür. */}
              {assignCtx.isCreator &&
                kind === "grup" &&
                can("mesajlasma.participant.manage") &&
                can("mesajlasma.unit.send") && (
                  <div className="msj-members-saha">
                    <button
                      type="button"
                      className="msj-members-addsaha"
                      onClick={openSahaPicker}
                      disabled={Array.isArray(sahaUnits) && sahaUnits.length === 0}
                    >
                      <UserGroupIcon /> Saha personelini ekle
                    </button>
                    {Array.isArray(sahaUnits) && sahaUnits.length > 1 && (
                      <div className="msj-members-saha-units">
                        {sahaUnits.map((u) => (
                          <button key={u.id} type="button" className="msj-wizard-chip" onClick={() => doAddSaha(u.id)}>
                            {u.name}
                          </button>
                        ))}
                        <button type="button" className="msj-wizard-chip" onClick={() => setSahaUnits(null)}>
                          Vazgeç
                        </button>
                      </div>
                    )}
                    <small className="msj-members-saha-note">
                      Eklenenler yalnız <b>eklendikleri andan sonraki</b> mesajları görür.
                    </small>
                  </div>
                )}

              {/* GLOBAL bildirim sesi anahtarı — tüm sohbetler için tek kontrol.
                  Kapatınca hiçbir sohbet mesaj gidiş/gelişinde ses çıkarmaz. */}
              <div className="msj-members-settings">
                <button
                  type="button"
                  className={`msj-sound-toggle ${soundMuted ? "is-off" : "is-on"}`}
                  onClick={() => setSoundMuted(!soundMuted)}
                  aria-pressed={!soundMuted}
                  title={soundMuted ? "Bildirim sesini aç" : "Bildirim sesini kapat"}
                >
                  <span className="msj-sound-toggle-ic">
                    {soundMuted ? <SpeakerXMarkIcon /> : <SpeakerWaveIcon />}
                  </span>
                  <span className="msj-sound-toggle-txt">
                    <b>Bildirim sesi</b>
                    <small>
                      {soundMuted
                        ? "Kapalı — tüm sohbetlerde sessiz"
                        : "Açık — mesaj gelince/gidince çalar"}
                    </small>
                  </span>
                  <span className={`msj-switch ${soundMuted ? "" : "is-on"}`} aria-hidden="true">
                    <span className="msj-switch-knob" />
                  </span>
                </button>
              </div>

              {variant !== "task" && (
                <div className="msj-members-foot">
                  <button
                    type="button"
                    className="msj-members-del"
                    onClick={() => {
                      setMembersOpen(false);
                      setConfirmHide(true);
                    }}
                  >
                    <TrashIcon /> Sohbeti sil
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      {/* FAZ-49: mesaj silme onayı */}
      <ConfirmDialog
        open={!!deleteFor}
        title="Mesajı sil"
        text="Bu mesajı silmek istiyor musunuz? Yerinde 'Bu mesaj silindi' yazısı kalır ve geri alınamaz."
        confirmLabel="Sil"
        onConfirm={doDeleteMessage}
        onCancel={() => setDeleteFor(null)}
      />

      {/* FAZ-55: "onaya gönder" SON ONAY modalı — yazılan metin + eklerin küçük
          resimleri. Onaylanınca: önce ekler sohbete gider (görev kapanınca
          sohbet kilitlenir → sonrasında ek yüklenemez), sonra complete. */}
      <ConfirmDialog
        open={!!teslimOnay}
        title="Onaya göndermeden önce kontrol edin"
        confirmLabel="Onayla ve gönder"
        confirmClassName="msj-confirm-primary"
        text={
          teslimOnay ? (
            <div className="msj-teslim-onay">
              <div className="msj-teslim-not">{teslimOnay.note}</div>
              {staged.length > 0 ? (
                <>
                  <div className="msj-teslim-baslik">{staged.length} ek gönderilecek:</div>
                  <div className="msj-teslim-ekler">
                    {staged.map((s) => (
                      <span key={`to-${s.id}`} className="msj-teslim-ek">
                        {s.isImage ? (
                          <img src={s.url} alt="" />
                        ) : (
                          <span className="msj-teslim-belge">
                            <DocumentIcon />
                            {s.file.name}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="msj-teslim-baslik">Ek gönderilmeyecek.</div>
              )}
            </div>
          ) : (
            ""
          )
        }
        onConfirm={async () => {
          const note = teslimOnay?.note || "";
          setTeslimOnay(null);
          if (!note) return;
          // FAZ-55/16 — TESLİM TEK MESAJ. Eskiden her ek ayrı mesaj, not ayrı
          // mesajdı → üç fotoğraflı teslim sohbette dört baloncuk üretiyordu.
          // Artık: notu ve BÜTÜN ekleri tek yüklemede gönder, o mesajın id'sini
          // complete'e ver → backend ikinci bir mesaj yazmaz, o mesajı "teslim"
          // tonuna boyar (mor kart).
          let mesajId = null;
          if (staged.length > 0) {
            setSending(true);
            try {
              const msg = await uploadChatFiles(
                conversationId,
                staged.map((s) => s.file),
                note,
                "teslim"
              );
              mesajId = msg?.id || null;
              pushOwn(msg);
              clearStaged();
            } catch (err) {
              toast.error(err?.response?.data?.file || err?.response?.data?.detail || "Ekler gönderilemedi.");
            } finally {
              setSending(false);
            }
          }
          await doTaskAction("complete", { note, message_id: mesajId });
        }}
        onCancel={() => setTeslimOnay(null)}
      />

      {/* FAZ-B: üye çıkarma onayı (yalnız kurucu) — FAZ-38 ortak ConfirmDialog */}
      <ConfirmDialog
        open={!!confirmRemove}
        title="Gruptan çıkar"
        text={
          confirmRemove
            ? `${confirmRemove.name} kişisini gruptan çıkarmak istiyor musunuz? Çıkarılan kişi bu sohbeti artık göremez.`
            : ""
        }
        confirmLabel="Çıkar"
        onConfirm={doRemoveMember}
        onCancel={() => setConfirmRemove(null)}
      />


      {/* "Sohbeti sil" onayı — yalnız kendi görünümünden gizler (E1) */}
      {confirmHide && (
        // FAZ-C: portal YOK — .msj-thread içine gömülü (tüm ekranı ORTALAMAZ,
        // sohbetin içinde açılır; .msj-confirm position:absolute).
        <div className="msj-confirm" onClick={() => setConfirmHide(false)} role="dialog" aria-modal="true">
          <div className="msj-confirm-panel" onClick={(e) => e.stopPropagation()}>
            <div className="msj-confirm-title">Sohbeti sil</div>
            <div className="msj-confirm-text">
              Bu sohbeti listenizden kaldırmak istiyor musunuz? İşlem{" "}
              <b>yalnız sizin görünümünüzden</b> kaldırır — talep/başvuru ve diğer
              katılımcılar etkilenmez. Yeni mesaj gelirse sohbet tekrar görünür.
            </div>
            <div className="msj-confirm-actions">
              <button type="button" className="btn-md btn-md-ghost" onClick={() => setConfirmHide(false)}>
                Vazgeç
              </button>
              <button type="button" className="btn-md msj-confirm-danger" onClick={doHide}>
                Kaldır
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
